import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useSearchParams } from 'react-router-dom';
import { authFetch, authJsonFetch } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Crown, 
  Palette,
  User,
  Mail,
  Shield,
  Users,
  Database,
  Folder,
  Plus,
  Edit,
  Trash2,
  Loader2,
  FlaskConical,
  AlertTriangle,
  Archive,
  CheckCircle,
  XCircle,
  X,
  Send,
  FileDown,
  FileSpreadsheet,
  Calendar,
  Clock,
  RefreshCw,
  Upload,
  Activity,
  ShieldCheck,
  UserCog,
  LogOut,
  Cloud,
  CloudOff,
  Download,
  Key,
  RotateCcw,
  FileText,
  Eye,
  Wand2,
  ArrowRight,
  Info,
  HardDrive,
  Server,
  Link,
  Unlink,
  Copy,
  ExternalLink,
  Building2,
  FileWarning,
  ChevronUp,
  ChevronDown,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import PointInTimeRecoveryDialog from '../components/PointInTimeRecoveryDialog';
import LegacyReconcileDialog from '../components/LegacyReconcileDialog';
import { FeatureFlagsPanel } from '../components/FeatureFlagsPanel';
import ErrorLogsPanel from '../components/ErrorLogsPanel';
import SavedSearchAuditPanel from '../components/SavedSearchAuditPanel';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

// Notes Import Errors Panel Component
const ImportErrorsPanel = ({ isBarbiePink }) => {
  const [rows, setRows] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [category, setCategory] = React.useState('');
  const [page, setPage] = React.useState(0);
  const PAGE_SIZE = 50;

  const fetchRows = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        skip: String(page * PAGE_SIZE),
      });
      if (category) params.set('category', category);
      const response = await authFetch(`${API}/import-data/notes/errors?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setRows(data.data || []);
        setTotal(data.total || 0);
      }
    } catch (err) {
      console.error('Failed to fetch import errors:', err);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => { fetchRows(); }, [category, page]);

  const exportCsv = () => {
    if (!rows.length) return;
    const header = ['When', 'Category', 'Reason', 'Organisation', 'Date', 'Content', 'Filename', 'User', 'Import ID'];
    const lines = [header.join(',')];
    rows.forEach(r => {
      const row = [r.created_at, r.category, r.reason, r.organization, r.date, r.content, r.filename, r.user_email, r.import_id]
        .map(v => `"${(v || '').toString().replace(/"/g, '""')}"`).join(',');
      lines.push(row);
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `notes-import-errors-${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const categoryColor = (c) => {
    if (c === 'duplicate') return 'bg-amber-100 text-amber-800 border-amber-300';
    if (c === 'skipped') return 'bg-slate-100 text-slate-800 border-slate-300';
    if (c === 'error') return 'bg-red-100 text-red-800 border-red-300';
    if (c === 'fatal') return 'bg-red-200 text-red-900 border-red-400';
    return 'bg-gray-100 text-gray-800 border-gray-300';
  };

  return (
    <Card className={cn(isBarbiePink && "card-brutalist")}>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileWarning className="w-5 h-5 text-amber-500" />
              Notes Import Error Log
            </CardTitle>
            <CardDescription>
              All skipped, duplicate, and error rows from past Notes Imports. Total: {total} entries.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Select value={category || 'all'} onValueChange={(v) => { setPage(0); setCategory(v === 'all' ? '' : v); }}>
              <SelectTrigger className="w-[140px]" data-testid="import-errors-category-filter">
                <SelectValue placeholder="All categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                <SelectItem value="duplicate">Duplicate</SelectItem>
                <SelectItem value="skipped">Skipped</SelectItem>
                <SelectItem value="error">Error</SelectItem>
                <SelectItem value="fatal">Fatal</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={fetchRows} disabled={loading} data-testid="import-errors-refresh">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </Button>
            <Button variant="outline" size="sm" onClick={exportCsv} disabled={!rows.length} data-testid="import-errors-export-csv">
              <Download className="w-4 h-4 mr-1" /> Export CSV
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-8 text-muted-foreground">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
            Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">No import errors logged yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2 font-medium">When</th>
                  <th className="text-left p-2 font-medium">Category</th>
                  <th className="text-left p-2 font-medium">Organisation</th>
                  <th className="text-left p-2 font-medium">Date</th>
                  <th className="text-left p-2 font-medium">Reason</th>
                  <th className="text-left p-2 font-medium">Content</th>
                  <th className="text-left p-2 font-medium">File</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.log_id} className="border-b hover:bg-muted/40">
                    <td className="p-2 whitespace-nowrap text-xs text-muted-foreground">
                      {r.created_at ? new Date(r.created_at).toLocaleString('en-AU', { timeZone: 'Australia/Sydney', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                    </td>
                    <td className="p-2">
                      <span className={cn("px-2 py-0.5 rounded text-xs border", categoryColor(r.category))}>
                        {r.category}
                      </span>
                    </td>
                    <td className="p-2 max-w-xs truncate" title={r.organization}>{r.organization}</td>
                    <td className="p-2 whitespace-nowrap">{r.date}</td>
                    <td className="p-2 max-w-xs" title={r.reason}>{r.reason}</td>
                    <td className="p-2 max-w-md truncate" title={r.content}>{r.content}</td>
                    <td className="p-2 max-w-[160px] truncate" title={r.filename}>{r.filename}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-3 text-sm">
                <span className="text-muted-foreground">Page {page + 1} of {totalPages}</span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))}>
                    Prev
                  </Button>
                  <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Sophie Errors Panel Component
const SophieErrorsPanel = ({ isBarbiePink }) => {
  const [errors, setErrors] = React.useState([]);
  const [totalErrors, setTotalErrors] = React.useState(0);
  const [loading, setLoading] = React.useState(false);

  const fetchErrors = async () => {
    setLoading(true);
    try {
      const response = await authFetch(`${API}/ai/assistant/errors`);
      if (response.ok) {
        const data = await response.json();
        setErrors(data.errors || []);
        setTotalErrors(data.total || 0);
      }
    } catch (err) {
      console.error('Failed to fetch Sophie errors:', err);
    } finally {
      setLoading(false);
    }
  };

  const clearErrors = async () => {
    if (!window.confirm('Clear all Sophie error logs?')) return;
    try {
      const response = await authFetch(`${API}/ai/assistant/errors`, { method: 'DELETE' });
      if (response.ok) {
        setErrors([]);
        setTotalErrors(0);
        toast.success('Sophie errors cleared');
      }
    } catch (err) {
      toast.error('Failed to clear errors');
    }
  };

  React.useEffect(() => { fetchErrors(); }, []);

  return (
    <Card className={cn(isBarbiePink && "card-brutalist")}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              Sophie AI Error Log
            </CardTitle>
            <CardDescription>
              Review errors encountered by Sophie during conversations. Total: {totalErrors} errors logged.
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchErrors} disabled={loading}>
              <RefreshCw className={cn("w-4 h-4 mr-1", loading && "animate-spin")} />
              Refresh
            </Button>
            {errors.length > 0 && (
              <Button variant="destructive" size="sm" onClick={clearErrors}>
                <Trash2 className="w-4 h-4 mr-1" />
                Clear All
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {errors.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">No errors logged. Sophie is running smoothly.</p>
        ) : (
          <div className="space-y-3 max-h-[500px] overflow-y-auto">
            {errors.map((err, idx) => (
              <div key={err.error_id || idx} className="p-3 rounded-lg border bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-mono bg-red-100 dark:bg-red-900/50 px-1.5 py-0.5 rounded text-red-700 dark:text-red-300">
                        {err.error_type || 'Error'}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {err.provider}/{err.model}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-red-800 dark:text-red-200 break-words">
                      {err.error_message}
                    </p>
                    {err.user_message && (
                      <p className="text-xs text-muted-foreground mt-1 truncate">
                        User asked: "{err.user_message}"
                      </p>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-xs text-muted-foreground">
                      {err.timestamp && new Date(err.timestamp).toLocaleString('en-AU', { timeZone: 'Australia/Sydney', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </p>
                    <p className="text-xs text-muted-foreground">{err.user_email}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};



// Transaction Logs Component for Disaster Recovery
const TransactionLogsCard = ({ isBarbiePink }) => {
  const [logFiles, setLogFiles] = React.useState({ local_files: [], drive_files: [] });
  const [selectedFile, setSelectedFile] = React.useState(null);
  const [selectedSource, setSelectedSource] = React.useState('database');
  const [previewData, setPreviewData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [previewLoading, setPreviewLoading] = React.useState(false);
  const [executing, setExecuting] = React.useState(false);
  const [uploadingFile, setUploadingFile] = React.useState(false);
  const [componentError, setComponentError] = React.useState(null);
  const fileInputRef = React.useRef(null);

  // Fetch available log files
  const fetchLogFiles = async () => {
    setLoading(true);
    try {
      const response = await authFetch(`${API}/admin/transaction-logs/files`);
      if (response.ok) {
        const data = await response.json();
        setLogFiles(data);
      }
    } catch (err) {
      console.error('Failed to fetch log files:', err);
      // Don't show error toast on initial load
    } finally {
      setLoading(false);
    }
  };

  // Upload local logs to Drive
  const uploadLogsToDrive = async () => {
    try {
      const response = await authFetch(`${API}/admin/transaction-logs/upload`, { method: 'POST' });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Uploaded ${data.uploaded?.length || 0} log files to Google Drive`);
        fetchLogFiles();
      } else {
        toast.error('Failed to upload transaction logs');
      }
    } catch (err) {
      toast.error('Failed to upload transaction logs');
    }
  };

  // Upload a local file
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.jsonl')) {
      toast.error('Please upload a .jsonl file');
      return;
    }

    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await authFetch(`${API}/admin/transaction-logs/upload-file`, {
        method: 'POST',
        body: formData
      });
      
      if (response.ok) {
        toast.success('Transaction log file uploaded successfully');
        fetchLogFiles();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to upload file');
      }
    } catch (err) {
      toast.error('Failed to upload file');
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Preview transactions from selected file or database
  const previewTransactions = async () => {
    if (selectedSource !== 'database' && !selectedFile) {
      toast.error('Please select a log file first');
      return;
    }

    setPreviewLoading(true);
    try {
      const params = new URLSearchParams({ source: selectedSource });
      if (selectedFile && selectedSource !== 'database') {
        params.append('filename', selectedFile);
      }
      
      const response = await authFetch(`${API}/admin/transaction-logs/preview?${params}`);
      if (response.ok) {
        const data = await response.json();
        setPreviewData(data);
      } else {
        toast.error('Failed to preview transactions');
      }
    } catch (err) {
      toast.error('Failed to preview transactions');
    } finally {
      setPreviewLoading(false);
    }
  };

  // Execute replay
  const executeReplay = async (dryRun = true) => {
    if (!selectedFile) {
      toast.error('Please select a log file first');
      return;
    }

    if (!dryRun && !window.confirm('Are you sure you want to replay transactions? This will modify the database. Make sure you have a recent backup.')) {
      return;
    }

    setExecuting(true);
    try {
      const response = await authJsonFetch(`${API}/admin/transaction-logs/replay-file`, {
        method: 'POST',
        body: JSON.stringify({ 
          dry_run: dryRun, 
          source: selectedSource,
          filename: selectedFile
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (dryRun) {
          toast.success(`Dry run: ${data.transactions_count} transactions would be replayed (${data.summary?.insert || 0} inserts, ${data.summary?.update || 0} updates, ${data.summary?.delete || 0} deletes)`);
        } else {
          toast.success(`Successfully replayed ${data.transactions_count} transactions`);
        }
        setPreviewData(data);
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to replay transactions');
      }
    } catch (err) {
      toast.error('Failed to replay transactions');
    } finally {
      setExecuting(false);
    }
  };

  React.useEffect(() => {
    fetchLogFiles();
  }, []);

  const currentFiles = selectedSource === 'local' ? logFiles.local_files : logFiles.drive_files;

  return (
    <Card className={cn(isBarbiePink && "card-brutalist")}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-primary" />
          Transaction Logs (Disaster Recovery)
        </CardTitle>
        <CardDescription>
          Database transaction logs for disaster recovery. Select a log file to preview and replay transactions.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Upload Actions */}
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={uploadLogsToDrive} size="sm">
            <Upload className="w-4 h-4 mr-2" />
            Sync to Drive
          </Button>
          <Button variant="outline" onClick={fetchLogFiles} size="sm" disabled={loading}>
            <RefreshCw className={cn("w-4 h-4 mr-2", loading && "animate-spin")} />
            Refresh
          </Button>
          <div className="relative">
            <input
              type="file"
              ref={fileInputRef}
              accept=".jsonl"
              onChange={handleFileUpload}
              className="hidden"
            />
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingFile}
            >
              {uploadingFile ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              Upload Log File
            </Button>
          </div>
        </div>

        {/* Source & File Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Source</Label>
            <Select value={selectedSource} onValueChange={(v) => { setSelectedSource(v); setSelectedFile(null); setPreviewData(null); }}>
              <SelectTrigger>
                <SelectValue placeholder="Select source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="database">Database ({logFiles.db_transactions_count || 0} transactions)</SelectItem>
                <SelectItem value="local">Local Files ({logFiles.local_files?.length || 0})</SelectItem>
                <SelectItem value="drive">Google Drive ({logFiles.drive_files?.length || 0})</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {selectedSource !== 'database' && (
          <div className="space-y-2">
            <Label>Log File</Label>
            <Select value={selectedFile || ''} onValueChange={(v) => v && setSelectedFile(v)}>
              <SelectTrigger>
                <SelectValue placeholder="Select a log file" />
              </SelectTrigger>
              <SelectContent>
                {currentFiles && currentFiles.length > 0 ? (
                  currentFiles.map((file, idx) => (
                    <SelectItem key={`${selectedSource}-${file.file_id || file.filename}-${idx}`} value={file.filename}>
                      {file.filename} ({file.size_kb || 0} KB)
                    </SelectItem>
                  ))
                ) : (
                  <div className="p-2 text-sm text-muted-foreground">No files available</div>
                )}
              </SelectContent>
            </Select>
          </div>
          )}
        </div>

        {/* Preview & Execute Actions */}
        <div className="flex flex-wrap gap-2">
          <Button 
            variant="outline" 
            onClick={previewTransactions}
            disabled={(selectedSource !== 'database' && !selectedFile) || previewLoading}
          >
            {previewLoading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Eye className="w-4 h-4 mr-2" />
            )}
            Preview Transactions
          </Button>
          <Button 
            variant="outline" 
            onClick={() => executeReplay(true)}
            disabled={!selectedFile || executing}
          >
            {executing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RotateCcw className="w-4 h-4 mr-2" />
            )}
            Dry Run
          </Button>
          <Button 
            variant="destructive"
            onClick={() => executeReplay(false)}
            disabled={!selectedFile || executing}
          >
            {executing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <AlertTriangle className="w-4 h-4 mr-2" />
            )}
            Execute Replay
          </Button>
        </div>

        {/* Preview Results */}
        {previewData && (
          <div className="p-4 border rounded-lg bg-muted/30 space-y-3">
            <h4 className="font-semibold flex items-center gap-2">
              <FileText className="w-4 h-4" />
              {previewData.dry_run !== false ? 'Preview' : 'Execution'} Results
            </h4>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="p-2 bg-background rounded border">
                <div className="text-muted-foreground">Total Transactions</div>
                <div className="text-xl font-bold">{previewData.transactions_count || previewData.count || 0}</div>
              </div>
              <div className="p-2 bg-background rounded border">
                <div className="text-muted-foreground">Inserts</div>
                <div className="text-xl font-bold text-green-600">{previewData.summary?.insert || 0}</div>
              </div>
              <div className="p-2 bg-background rounded border">
                <div className="text-muted-foreground">Updates</div>
                <div className="text-xl font-bold text-blue-600">{previewData.summary?.update || 0}</div>
              </div>
              <div className="p-2 bg-background rounded border">
                <div className="text-muted-foreground">Deletes</div>
                <div className="text-xl font-bold text-red-600">{previewData.summary?.delete || 0}</div>
              </div>
            </div>

            {previewData.transactions && previewData.transactions.length > 0 && (
              <div className="max-h-64 overflow-auto border rounded">
                <Table>
                  <TableHeader className="sticky top-0 bg-background">
                    <TableRow>
                      <TableHead className="text-xs">Timestamp</TableHead>
                      <TableHead className="text-xs">Operation</TableHead>
                      <TableHead className="text-xs">Collection</TableHead>
                      <TableHead className="text-xs">Document ID</TableHead>
                      <TableHead className="text-xs">User</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {previewData.transactions.slice(0, 50).map((tx, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="text-xs">{new Date(tx.timestamp).toLocaleString('en-AU', { timeZone: 'Australia/Sydney' })}</TableCell>
                        <TableCell className="text-xs">
                          <Badge variant={tx.operation === 'INSERT' ? 'default' : tx.operation === 'DELETE' ? 'destructive' : 'secondary'}>
                            {tx.operation}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs">{tx.collection || '-'}</TableCell>
                        <TableCell className="text-xs font-mono">{tx.document_id ? tx.document_id.slice(0, 15) + '...' : '-'}</TableCell>
                        <TableCell className="text-xs">{tx.user_email || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {previewData.transactions.length > 50 && (
                  <p className="text-xs text-muted-foreground p-2 text-center">
                    Showing 50 of {previewData.transactions.length} transactions
                  </p>
                )}
              </div>
            )}

            {previewData.summary?.errors?.length > 0 && (
              <div className="p-2 bg-destructive/10 border border-destructive rounded">
                <h5 className="text-sm font-semibold text-destructive">Errors ({previewData.summary.errors.length})</h5>
                <ul className="text-xs text-destructive">
                  {previewData.summary.errors.slice(0, 5).map((err, idx) => (
                    <li key={idx}>{err.transaction}: {err.error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// AI Provider Settings Component (Admin Only)
const AIProviderSettings = ({ isBarbiePink }) => {
  const [settings, setSettings] = React.useState({
    provider: 'gemini',
    model: 'gemini-3-flash-preview',
    temperature: 0.7,
    has_openai_key: false,
    has_anthropic_key: false,
    has_gemini_key: true
  });
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [openaiKey, setOpenaiKey] = React.useState('');
  const [anthropicKey, setAnthropicKey] = React.useState('');
  const [geminiKey, setGeminiKey] = React.useState('');

  const providers = [
    { value: 'gemini', label: 'Google Gemini', icon: '🌟' },
    { value: 'openai', label: 'OpenAI', icon: '🤖' },
    { value: 'anthropic', label: 'Anthropic Claude', icon: '🧠' }
  ];

  const modelOptions = {
    gemini: [
      { value: 'gemini-3-flash-preview', label: 'Gemini 3 Flash (Recommended)' },
      { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro' },
      { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash' },
      { value: 'gemini-2.0-flash', label: 'Gemini 2.0 Flash' }
    ],
    openai: [
      { value: 'gpt-4o', label: 'GPT-4o (Recommended)' },
      { value: 'gpt-5.2', label: 'GPT-5.2' },
      { value: 'gpt-4.1', label: 'GPT-4.1' },
      { value: 'gpt-4.1-mini', label: 'GPT-4.1 Mini' }
    ],
    anthropic: [
      { value: 'claude-sonnet-4-5-20250929', label: 'Claude Sonnet 4.5 (Recommended)' },
      { value: 'claude-4-sonnet-20250514', label: 'Claude 4 Sonnet' },
      { value: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5' }
    ]
  };

  React.useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await authFetch(`${API}/admin/ai-provider/settings`);
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (err) {
      console.error('Failed to fetch AI provider settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const payload = {
        provider: settings.provider,
        model: settings.model,
        temperature: settings.temperature
      };
      
      // Only include keys if they were entered
      if (openaiKey) payload.openai_api_key = openaiKey;
      if (anthropicKey) payload.anthropic_api_key = anthropicKey;
      if (geminiKey) payload.gemini_api_key = geminiKey;

      const response = await authJsonFetch(`${API}/admin/ai-provider/settings`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success('AI provider settings saved');
        // Clear key inputs after save
        setOpenaiKey('');
        setAnthropicKey('');
        setGeminiKey('');
        fetchSettings();
      } else {
        toast.error('Failed to save settings');
      }
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-10 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn(isBarbiePink && "card-brutalist")}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-primary" />
          AI Provider Configuration
        </CardTitle>
        <CardDescription>
          Configure which AI provider and model to use for OrderMerge Pro and other AI features.
          Switch providers for fallback if one service has issues.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Current Active Model Indicator */}
        <div className={cn(
          "p-4 rounded-lg border-2",
          settings.provider === 'gemini' && "bg-blue-50 border-blue-300 dark:bg-blue-950 dark:border-blue-700",
          settings.provider === 'openai' && "bg-green-50 border-green-300 dark:bg-green-950 dark:border-green-700",
          settings.provider === 'anthropic' && "bg-purple-50 border-purple-300 dark:bg-purple-950 dark:border-purple-700"
        )}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Currently Active</p>
              <p className="text-lg font-semibold">
                {providers.find(p => p.value === settings.provider)?.label || settings.provider}
              </p>
              <p className="text-sm text-muted-foreground">
                Model: {modelOptions[settings.provider]?.find(m => m.value === settings.model)?.label || settings.model}
              </p>
            </div>
            <div className={cn(
              "w-12 h-12 rounded-full flex items-center justify-center text-2xl",
              settings.provider === 'gemini' && "bg-blue-500 text-white",
              settings.provider === 'openai' && "bg-green-500 text-white",
              settings.provider === 'anthropic' && "bg-purple-500 text-white"
            )}>
              {providers.find(p => p.value === settings.provider)?.icon}
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Sophie AI and all AI features will use this provider and model.
          </p>
        </div>

        {/* Provider Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>AI Provider</Label>
            <Select 
              value={settings.provider} 
              onValueChange={(v) => setSettings({
                ...settings, 
                provider: v,
                model: modelOptions[v]?.[0]?.value || ''
              })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select provider" />
              </SelectTrigger>
              <SelectContent>
                {providers.map(p => (
                  <SelectItem key={p.value} value={p.value}>
                    <span className="flex items-center gap-2">
                      <span>{p.icon}</span>
                      <span>{p.label}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Model</Label>
            <Select 
              value={settings.model} 
              onValueChange={(v) => setSettings({...settings, model: v})}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select model" />
              </SelectTrigger>
              <SelectContent>
                {(modelOptions[settings.provider] || []).map(m => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Temperature Slider */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label>AI Temperature</Label>
            <span className="text-sm font-medium px-2 py-1 bg-muted rounded">
              {settings.temperature?.toFixed(2) || '0.70'}
            </span>
          </div>
          <Slider
            value={[settings.temperature || 0.7]}
            onValueChange={(value) => setSettings({...settings, temperature: value[0]})}
            min={0}
            max={1}
            step={0.05}
            className={cn("w-full", isBarbiePink && "[&_[role=slider]]:bg-pink-500")}
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>0.0 - More Focused</span>
            <span>0.5 - Balanced</span>
            <span>1.0 - More Creative</span>
          </div>
          <p className="text-xs text-muted-foreground">
            Lower values make the AI more deterministic and focused. Higher values increase creativity and variability.
          </p>
        </div>

        {/* API Key Status */}
        <div className="p-4 bg-muted/50 rounded-lg space-y-3">
          <h4 className="font-semibold text-sm">API Key Status</h4>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className={cn("w-2 h-2 rounded-full", settings.has_gemini_key ? "bg-green-500" : "bg-gray-300")} />
              <span>Gemini: {settings.has_gemini_key ? 'Configured' : 'Not set'}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className={cn("w-2 h-2 rounded-full", settings.has_openai_key ? "bg-green-500" : "bg-gray-300")} />
              <span>OpenAI: {settings.has_openai_key ? 'Configured' : 'Not set'}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className={cn("w-2 h-2 rounded-full", settings.has_anthropic_key ? "bg-green-500" : "bg-gray-300")} />
              <span>Anthropic: {settings.has_anthropic_key ? 'Configured' : 'Not set'}</span>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Note: Gemini uses the Emergent LLM Key by default. Add custom keys below if needed.
          </p>
        </div>

        {/* API Key Inputs */}
        <div className="space-y-4">
          <h4 className="font-semibold text-sm">Update API Keys (Optional)</h4>
          
          <div className="space-y-2">
            <Label className="text-xs">OpenAI API Key</Label>
            <Input
              type="password"
              placeholder="sk-..."
              value={openaiKey}
              onChange={(e) => setOpenaiKey(e.target.value)}
            />
          </div>
          
          <div className="space-y-2">
            <Label className="text-xs">Anthropic API Key</Label>
            <Input
              type="password"
              placeholder="sk-ant-..."
              value={anthropicKey}
              onChange={(e) => setAnthropicKey(e.target.value)}
            />
          </div>
          
          <div className="space-y-2">
            <Label className="text-xs">Custom Gemini API Key (Override default)</Label>
            <Input
              type="password"
              placeholder="AIza..."
              value={geminiKey}
              onChange={(e) => setGeminiKey(e.target.value)}
            />
          </div>
        </div>

        {/* Save Button */}
        <Button 
          onClick={saveSettings} 
          disabled={saving}
          className={cn(isBarbiePink && "bg-pink-500 hover:bg-pink-600")}
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <CheckCircle className="w-4 h-4 mr-2" />
              Save Settings
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

export const SettingsPage = () => {
  const { user } = useAuth();
  const { theme, setTheme, isBarbiePink, isRoyalBlue } = useTheme();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('appearance');
  const [sectors, setSectors] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sectorDialogOpen, setSectorDialogOpen] = useState(false);
  const [editingSector, setEditingSector] = useState(null);
  const [stressTestDialogOpen, setStressTestDialogOpen] = useState(false);
  const [stressTestLoading, setStressTestLoading] = useState(false);
  const [sectorForm, setSectorForm] = useState({
    name: '',
    display_name: '',
    color: 'gray'
  });
  
  // Reports state
  const [reportEmail, setReportEmail] = useState('');
  const [reportLoading, setReportLoading] = useState(false);
  const [sendingToRecipient, setSendingToRecipient] = useState(null); // Track which recipient is being sent to
  const [backups, setBackups] = useState([]);
  const [reportPreview, setReportPreview] = useState('');
  const [reportRecipients, setReportRecipients] = useState([]);
  const [newRecipient, setNewRecipient] = useState('');
  
  // Backup error logs state
  const [backupErrorLogs, setBackupErrorLogs] = useState([]);
  const [showBackupLogs, setShowBackupLogs] = useState(false);
  const [loadingBackupLogs, setLoadingBackupLogs] = useState(false);
  
  // Gmail integration state
  const [gmailStatus, setGmailStatus] = useState(null);
  const [gmailLoading, setGmailLoading] = useState(false);
  
  // GCS integration state
  const [gcsStatus, setGcsStatus] = useState(null);
  const [gcsLoading, setGcsLoading] = useState(false);
  const [gcsTesting, setGcsTesting] = useState(false);
  const [gcsUploadingCreds, setGcsUploadingCreds] = useState(false);
  
  // User management state
  const [userToDelete, setUserToDelete] = useState(null);
  const [userToUpdateRole, setUserToUpdateRole] = useState(null);
  const [userToForceLogout, setUserToForceLogout] = useState(null);
  const [userActionLoading, setUserActionLoading] = useState(false);
  
  // Database management state
  const [consistencyReport, setConsistencyReport] = useState(null);
  const [dbHealthExpanded, setDbHealthExpanded] = useState(false);
  const [dbToolsExpanded, setDbToolsExpanded] = useState(false);
  const [pitrDialogOpen, setPitrDialogOpen] = useState(false);
  const [reconcileDialogOpen, setReconcileDialogOpen] = useState(false);
  const [diagRecipient, setDiagRecipient] = useState('');
  const [diagRunning, setDiagRunning] = useState(false);
  const [diagResult, setDiagResult] = useState(null);

  const runGmailDiagnostic = async () => {
    setDiagRunning(true);
    setDiagResult(null);
    try {
      const res = await fetch(`${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api/diagnose-gmail`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipient: (diagRecipient || '').trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || `HTTP ${res.status}`);
        setDiagResult({
          has_token: false,
          token_email: null,
          token_expired: null,
          test_send_status: 'failed',
          test_send_error: data.detail || `HTTP ${res.status}`,
        });
        return;
      }
      setDiagResult(data);
      if (data.test_send_status === 'sent') {
        toast.success('Diagnostic email sent — check inbox + spam.');
      } else if (data.test_send_status === 'failed') {
        toast.error('Diagnostic FAILED — see details below.');
      } else {
        toast.warning('Diagnostic skipped — no Gmail token connected.');
      }
    } catch (e) {
      toast.error(`Diagnostic request failed: ${e.message}`);
    } finally {
      setDiagRunning(false);
    }
  };
  const [consistencyLoading, setConsistencyLoading] = useState(false);
  const [primaryEnforceLoading, setPrimaryEnforceLoading] = useState(false);
  const [primaryEnforceResult, setPrimaryEnforceResult] = useState(null);
  const [orphanOrgsLoading, setOrphanOrgsLoading] = useState(false);
  const [orphanOrgsResult, setOrphanOrgsResult] = useState(null);
  const [orphanContactsLoading, setOrphanContactsLoading] = useState(false);
  const [orphanContactsResult, setOrphanContactsResult] = useState(null);
  // Cross-org notes scanner state
  const [crossOrgScanLoading, setCrossOrgScanLoading] = useState(false);
  const [crossOrgScanStatus, setCrossOrgScanStatus] = useState(null);
  const [crossOrgScanFilter, setCrossOrgScanFilter] = useState('');
  // Clear comm notes state (with optional date range)
  const [clearNotesLoading, setClearNotesLoading] = useState(false);
  const [clearNotesResult, setClearNotesResult] = useState(null);
  const [clearNotesDialogOpen, setClearNotesDialogOpen] = useState(false);
  const [clearNotesFromDate, setClearNotesFromDate] = useState('');
  const [clearNotesToDate, setClearNotesToDate] = useState('');
  const [clearNotesPreview, setClearNotesPreview] = useState(null);
  const [clearNotesPreviewLoading, setClearNotesPreviewLoading] = useState(false);
  const [clearNotesConfirmText, setClearNotesConfirmText] = useState('');
  const [backupListExpanded, setBackupListExpanded] = useState(false);
  // Normalize-dates migration state
  const [normalizeDatesOpen, setNormalizeDatesOpen] = useState(false);
  const [normalizeDatesLoading, setNormalizeDatesLoading] = useState(false);
  const [normalizeDatesDry, setNormalizeDatesDry] = useState(null);
  const [normalizeDatesExecuted, setNormalizeDatesExecuted] = useState(null);
  const [normalizeDatesConfirm, setNormalizeDatesConfirm] = useState('');
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoreLoading, setRestoreLoading] = useState(false);
  // Live progress string from the backend's polling endpoint. Rendered
  // inline in the Restore dialog so long restores are visibly working.
  const [restoreProgress, setRestoreProgress] = useState('');
  const [preserveEmailTemplates, setPreserveEmailTemplates] = useState(false);
  const [preserveWorkflowTemplates, setPreserveWorkflowTemplates] = useState(false);
  const [includeSystemCollections, setIncludeSystemCollections] = useState(true);
  const [backupToDelete, setBackupToDelete] = useState(null);
  const [clearAllDataDialog, setClearAllDataDialog] = useState(false);
  const [clearDataLoading, setClearDataLoading] = useState(false);
  
  // XLS Import/Export state
  const [xlsExportLoading, setXlsExportLoading] = useState(false);
  const [xlsImportDialogOpen, setXlsImportDialogOpen] = useState(false);
  const [xlsUploadLoading, setXlsUploadLoading] = useState(false);
  const [xlsPreviewData, setXlsPreviewData] = useState(null);
  const [xlsImportLoading, setXlsImportLoading] = useState(false);
  const [xlsImportMode, setXlsImportMode] = useState('append');
  const [xlsSelectedSheets, setXlsSelectedSheets] = useState([]);
  
  // Smart Import (Field Mapping) state
  const [smartImportStep, setSmartImportStep] = useState(1); // 1: upload, 2: select sheet, 3: map fields, 4: import
  const [selectedImportSheet, setSelectedImportSheet] = useState('');
  const [dbFields, setDbFields] = useState([]);
  const [fieldMappings, setFieldMappings] = useState({}); // {xls_column: db_field}
  const [matchField, setMatchField] = useState('org_name');
  const [updateExisting, setUpdateExisting] = useState(true);
  const [createNew, setCreateNew] = useState(false);
  const [importResults, setImportResults] = useState(null);
  
  // Calendar configuration state
  const [calendarId, setCalendarId] = useState('');
  const [calendarLoading, setCalendarLoading] = useState(false);
  
  // Disaster Recovery state
  const [dbConnectionInfo, setDbConnectionInfo] = useState(null);
  const [loadingDbInfo, setLoadingDbInfo] = useState(false);
  const [showMigrationDialog, setShowMigrationDialog] = useState(false);
  const [targetMongoUrl, setTargetMongoUrl] = useState('');
  const [targetDbName, setTargetDbName] = useState('');
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionTestResult, setConnectionTestResult] = useState(null);
  const [migrating, setMigrating] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);
  const [includeUsersInMigration, setIncludeUsersInMigration] = useState(false);
  const [migrationLogs, setMigrationLogs] = useState([]);
  
  // Secrets panel state
  const [secrets, setSecrets] = useState(null);
  const [loadingSecrets, setLoadingSecrets] = useState(false);
  const [showSecrets, setShowSecrets] = useState(false);

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (isAdmin) {
      fetchSectors();
      fetchUsers();
      fetchBackups();
      fetchReportRecipients();
      fetchGcsStatus();
      fetchCalendarConfig();
    }
    fetchGmailStatus();
    
    // Check for Gmail callback success
    if (searchParams.get('gmail') === 'connected') {
      toast.success('Gmail & Drive connected successfully!');
      fetchGmailStatus();
    }
  }, [isAdmin, searchParams]);

  const fetchCalendarConfig = async () => {
    try {
      const response = await authFetch(`${API}/calendar/config`);
      if (response.ok) {
        const data = await response.json();
        setCalendarId(data.calendar_id || '');
      }
    } catch (err) {
      console.error('Failed to fetch calendar config:', err);
    }
  };

  const saveCalendarConfig = async () => {
    if (!calendarId.trim()) {
      toast.error('Please enter a calendar ID');
      return;
    }
    
    setCalendarLoading(true);
    try {
      const response = await authJsonFetch(`${API}/calendar/config`, {
        method: 'POST',
        body: JSON.stringify({ calendar_id: calendarId.trim() })
      });
      
      if (response.ok) {
        toast.success('Calendar configuration saved');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save configuration');
      }
    } catch (err) {
      toast.error('Failed to save calendar configuration');
    } finally {
      setCalendarLoading(false);
    }
  };

  // Disaster Recovery Functions
  const fetchDbConnectionInfo = async () => {
    setLoadingDbInfo(true);
    try {
      const response = await authFetch(`${API}/admin/database/connection-info`);
      if (response.ok) {
        const data = await response.json();
        setDbConnectionInfo(data);
      }
    } catch (err) {
      console.error('Failed to fetch DB connection info:', err);
    } finally {
      setLoadingDbInfo(false);
    }
  };

  const fetchMigrationLogs = async () => {
    try {
      const response = await authFetch(`${API}/admin/database/migration-logs`);
      if (response.ok) {
        const data = await response.json();
        setMigrationLogs(data.logs || []);
      }
    } catch (err) {
      console.error('Failed to fetch migration logs:', err);
    }
  };

  const testNewDbConnection = async () => {
    if (!targetMongoUrl || !targetDbName) {
      toast.error('Please enter MongoDB URL and database name');
      return;
    }
    
    setTestingConnection(true);
    setConnectionTestResult(null);
    
    try {
      const formData = new FormData();
      formData.append('mongo_url', targetMongoUrl);
      formData.append('db_name', targetDbName);
      
      const response = await authFetch(`${API}/admin/database/test-connection`, {
        method: 'POST',
        body: formData
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setConnectionTestResult({ success: true, ...data });
        toast.success('Connection successful!');
      } else {
        setConnectionTestResult({ success: false, error: data.detail });
        toast.error(data.detail || 'Connection failed');
      }
    } catch (err) {
      setConnectionTestResult({ success: false, error: err.message });
      toast.error('Connection test failed: ' + err.message);
    } finally {
      setTestingConnection(false);
    }
  };

  const executeMigration = async () => {
    if (!connectionTestResult?.success) {
      toast.error('Please test the connection first');
      return;
    }
    
    setMigrating(true);
    setMigrationResult(null);
    
    try {
      const formData = new FormData();
      formData.append('target_mongo_url', targetMongoUrl);
      formData.append('target_db_name', targetDbName);
      formData.append('include_users', includeUsersInMigration.toString());
      
      const response = await authFetch(`${API}/admin/database/migrate-to-new`, {
        method: 'POST',
        body: formData
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setMigrationResult({ success: true, ...data });
        toast.success(`Migration complete! ${data.total_documents} documents migrated.`);
        fetchMigrationLogs();
      } else {
        setMigrationResult({ success: false, error: data.detail });
        toast.error(data.detail || 'Migration failed');
      }
    } catch (err) {
      setMigrationResult({ success: false, error: err.message });
      toast.error('Migration failed: ' + err.message);
    } finally {
      setMigrating(false);
    }
  };

  const resetMigrationDialog = () => {
    setTargetMongoUrl('');
    setTargetDbName('');
    setConnectionTestResult(null);
    setMigrationResult(null);
    setIncludeUsersInMigration(false);
  };

  const fetchReportRecipients = async () => {
    try {
      const response = await authFetch(`${API}/reports/recipients`);
      if (response.ok) {
        const data = await response.json();
        setReportRecipients(data.recipients || []);
      }
    } catch (err) {
      console.error('Failed to fetch report recipients:', err);
    }
  };

  // Fetch admin secrets
  const fetchSecrets = async () => {
    setLoadingSecrets(true);
    try {
      const response = await authFetch(`${API}/admin/secrets`);
      if (response.ok) {
        const data = await response.json();
        setSecrets(data);
      } else {
        toast.error('Failed to fetch secrets');
      }
    } catch (err) {
      console.error('Failed to fetch secrets:', err);
      toast.error('Failed to load secrets');
    } finally {
      setLoadingSecrets(false);
    }
  };

  const addRecipient = async () => {
    if (!newRecipient || !newRecipient.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    if (reportRecipients.includes(newRecipient)) {
      toast.error('Email already in the list');
      return;
    }
    
    const updatedList = [...reportRecipients, newRecipient];
    try {
      const response = await authJsonFetch(`${API}/reports/recipients`, {
        method: 'PUT',
        body: JSON.stringify({ emails: updatedList })
      });
      
      if (response.ok) {
        setReportRecipients(updatedList);
        setNewRecipient('');
        toast.success('Recipient added');
      }
    } catch (err) {
      toast.error('Failed to add recipient');
    }
  };

  const removeRecipient = async (email) => {
    const updatedList = reportRecipients.filter(e => e !== email);
    try {
      const response = await authJsonFetch(`${API}/reports/recipients`, {
        method: 'PUT',
        body: JSON.stringify({ emails: updatedList })
      });
      
      if (response.ok) {
        setReportRecipients(updatedList);
        toast.success('Recipient removed');
      }
    } catch (err) {
      toast.error('Failed to remove recipient');
    }
  };

  const fetchGmailStatus = async () => {
    try {
      const response = await authFetch(`${API}/gmail/status`);
      if (response.ok) {
        setGmailStatus(await response.json());
      }
    } catch (err) {
      console.error('Failed to fetch Gmail status:', err);
    }
  };

  const connectGmail = async () => {
    setGmailLoading(true);
    try {
      const response = await authFetch(`${API}/gmail/connect`);
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.authorization_url;
      } else {
        toast.error('Failed to start Gmail connection');
      }
    } catch (err) {
      toast.error('An error occurred');
    } finally {
      setGmailLoading(false);
    }
  };

  const disconnectGmail = async () => {
    if (!window.confirm('Are you sure you want to disconnect Google services (Gmail & Drive)? You will no longer be able to send emails or edit templates in Google Docs.')) return;
    
    setGmailLoading(true);
    try {
      const response = await authFetch(`${API}/gmail/disconnect`, {
        method: 'POST'
      });
      if (response.ok) {
        toast.success('Google services disconnected successfully');
        setGmailStatus({ connected: false, gmail_email: null, drive_connected: false });
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || 'Failed to disconnect Google services');
      }
    } catch (err) {
      console.error('Disconnect error:', err);
      toast.error('An error occurred while disconnecting');
    } finally {
      setGmailLoading(false);
    }
  };

  // ==================== GCS FUNCTIONS ====================
  
  const fetchGcsStatus = async () => {
    setGcsLoading(true);
    try {
      const response = await authFetch(`${API}/admin/gcs/status`);
      if (response.ok) {
        const data = await response.json();
        setGcsStatus(data);
      }
    } catch (err) {
      console.error('Failed to fetch GCS status:', err);
    } finally {
      setGcsLoading(false);
    }
  };

  const uploadGcsCredentials = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setGcsUploadingCreds(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await authFetch(`${API}/admin/gcs/upload-credentials`, {
        method: 'POST',
        body: formData
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message);
        fetchGcsStatus();
      } else {
        toast.error(data.detail || 'Failed to upload credentials');
      }
    } catch (err) {
      toast.error('Failed to upload credentials');
    } finally {
      setGcsUploadingCreds(false);
      e.target.value = '';
    }
  };

  const testGcsConnection = async () => {
    setGcsTesting(true);
    try {
      const response = await authFetch(`${API}/admin/gcs/test`, { method: 'POST' });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(data.message);
      } else {
        toast.error(data.message);
      }
    } catch (err) {
      toast.error('Test failed');
    } finally {
      setGcsTesting(false);
    }
  };

  const disconnectGcs = async () => {
    if (!window.confirm('Are you sure you want to remove GCS credentials? Files will be stored locally only.')) return;
    
    setGcsLoading(true);
    try {
      const response = await authFetch(`${API}/admin/gcs/credentials`, { method: 'DELETE' });
      
      if (response.ok) {
        toast.success('GCS credentials removed');
        setGcsStatus({ connected: false, credentials_uploaded: false });
      } else {
        toast.error('Failed to remove credentials');
      }
    } catch (err) {
      toast.error('Failed to remove credentials');
    } finally {
      setGcsLoading(false);
    }
  };

  const sendTestEmail = async () => {
    setGmailLoading(true);
    try {
      const response = await authFetch(`${API}/gmail/test`);
      if (response.ok) {
        toast.success('Test email sent! Check your inbox.');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send test email');
      }
    } catch (err) {
      toast.error('An error occurred');
    } finally {
      setGmailLoading(false);
    }
  };

  // Report functions
  const [driveConnected, setDriveConnected] = useState(false);
  const [backupWarnings, setBackupWarnings] = useState([]);
  const [backupCounts, setBackupCounts] = useState({ local: 0, drive: 0, retention_days: 14 });

  const fetchBackups = async () => {
    try {
      const response = await authFetch(`${API}/reports/backups`);
      if (response.ok) {
        const data = await response.json();
        // Handle both old format (array) and new format (object with backups array)
        if (Array.isArray(data)) {
          setBackups(data);
          setBackupWarnings([]);
          setBackupCounts({ local: 0, drive: 0, retention_days: 14 });
        } else {
          setBackups(data.backups || []);
          setDriveConnected(data.drive_connected || false);
          setBackupWarnings(Array.isArray(data.warnings) ? data.warnings : []);
          setBackupCounts({
            local: data.local_count || 0,
            drive: data.drive_count || 0,
            retention_days: data.local_retention_days || 14,
          });
        }
      }
    } catch (err) {
      console.error('Failed to fetch backups:', err);
    }
  };

  const previewReport = async () => {
    setReportLoading(true);
    try {
      const response = await authFetch(`${API}/reports/preview`);
      if (response.ok) {
        const data = await response.json();
        setReportPreview(data.report);
      } else {
        toast.error('Failed to generate preview');
      }
    } catch (err) {
      toast.error('An error occurred');
    } finally {
      setReportLoading(false);
    }
  };

  const sendReportNow = async () => {
    if (!reportEmail) {
      toast.error('Please enter an email address');
      return;
    }
    
    setReportLoading(true);
    try {
      const response = await authJsonFetch(`${API}/reports/send-now`, {
        method: 'POST',
        body: JSON.stringify({ email: reportEmail, include_backup: true })  // Always include backup
      });
      
      if (response.ok) {
        toast.success(`Report sent to ${reportEmail} with database backup attached`);
        setReportEmail('');
      } else {
        let errorMsg = 'Failed to send report';
        try {
          const error = await response.json();
          errorMsg = error.detail || errorMsg;
        } catch {
          errorMsg = `Failed to send report (${response.status})`;
        }
        toast.error(errorMsg);
        console.error('Send report error:', errorMsg);
      }
    } catch (err) {
      console.error('Send report exception:', err);
      toast.error('An error occurred: ' + (err.message || 'Unknown error'));
    } finally {
      setReportLoading(false);
    }
  };

  // Send report to a specific recipient from the list
  const sendReportToRecipient = async (email) => {
    setSendingToRecipient(email);
    try {
      const response = await authJsonFetch(`${API}/reports/send-now`, {
        method: 'POST',
        body: JSON.stringify({ email: email, include_backup: true })
      });
      
      if (response.ok) {
        toast.success(`Report sent to ${email} with database backup attached`);
      } else {
        let errorMsg = 'Failed to send report';
        try {
          const error = await response.json();
          errorMsg = error.detail || errorMsg;
        } catch {
          errorMsg = `Failed to send report (${response.status})`;
        }
        toast.error(errorMsg);
        console.error('Send report error:', errorMsg);
      }
    } catch (err) {
      console.error('Send report exception:', err);
      toast.error('An error occurred: ' + (err.message || 'Unknown error'));
    } finally {
      setSendingToRecipient(null);
    }
  };

  const [backupJobId, setBackupJobId] = useState(null);
  
  // Poll for backup status
  const pollBackupStatus = async (jobId) => {
    const maxAttempts = 120; // Poll for up to 10 minutes (5 sec intervals)
    let attempts = 0;
    
    const checkStatus = async () => {
      try {
        const response = await authFetch(`${API}/reports/backup-status/${jobId}`);
        if (!response.ok) {
          throw new Error('Failed to check backup status');
        }
        
        const data = await response.json();
        
        if (data.status === 'completed') {
          toast.success(`Backup completed: ${data.filename}`);
          setReportLoading(false);
          setBackupJobId(null);
          fetchBackups();
          return;
        } else if (data.status === 'completed_local_only') {
          // Backup zip exists locally but Drive upload failed — explicit warning
          toast.warning(
            `Backup saved locally as ${data.filename} but Drive upload failed: ${data.warning || data.drive_error || 'unknown error'}. The pod disk is wiped on every deploy — reconnect Google in Settings, then re-run.`,
            { duration: 12000 }
          );
          setReportLoading(false);
          setBackupJobId(null);
          fetchBackups();
          return;
        } else if (data.status === 'failed') {
          toast.error(`Backup failed: ${data.error || 'Unknown error'}`);
          setReportLoading(false);
          setBackupJobId(null);
          return;
        } else if (data.status === 'running') {
          // Still running, continue polling
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(checkStatus, 5000); // Poll every 5 seconds
          } else {
            toast.warning('Backup is taking longer than expected. Check backup list in a few minutes.');
            setReportLoading(false);
            setBackupJobId(null);
          }
        } else {
          // Unknown status, refresh backup list
          toast.info('Backup status unknown. Refreshing backup list...');
          setReportLoading(false);
          setBackupJobId(null);
          fetchBackups();
        }
      } catch (err) {
        console.error('Error polling backup status:', err);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(checkStatus, 5000);
        } else {
          toast.warning('Could not verify backup status. Check backup list manually.');
          setReportLoading(false);
          setBackupJobId(null);
          fetchBackups();
        }
      }
    };
    
    checkStatus();
  };
  
  const createBackup = async () => {
    setReportLoading(true);
    try {
      toast.info('Starting backup... This runs in the background and may take several minutes.');
      
      const response = await authFetch(`${API}/reports/backup`, { 
        method: 'POST'
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.job_id) {
          setBackupJobId(data.job_id);
          toast.info(`Backup started (Job ID: ${data.job_id}). Monitoring progress...`);
          pollBackupStatus(data.job_id);
        } else if (data.filename) {
          // Immediate completion (small DB)
          toast.success('Backup created successfully');
          setReportLoading(false);
          fetchBackups();
        }
      } else {
        let errorMsg = 'Failed to start backup';
        try {
          const errorData = await response.json();
          errorMsg = errorData.detail || errorMsg;
        } catch {
          errorMsg = `Failed to start backup (${response.status})`;
        }
        toast.error(errorMsg);
        console.error('Backup error:', errorMsg);
        setReportLoading(false);
      }
    } catch (err) {
      console.error('Backup exception:', err);
      toast.error('An error occurred: ' + (err.message || 'Network error - please check your connection'));
      setReportLoading(false);
    }
  };

  // Create a backup and immediately download it
  const [downloadingDatabase, setDownloadingDatabase] = useState(false);
  const [downloadJobId, setDownloadJobId] = useState(null);
  
  // Function to download an existing backup file
  const triggerBackupDownload = async (filename) => {
    try {
      toast.info('Starting download... Large files may take a while.');
      
      // Get auth token from localStorage
      const token = localStorage.getItem('auth_token');
      
      // Create a temporary link that will stream the file directly
      const downloadUrl = `${API}/reports/download-backup/${filename}`;
      
      // Use fetch with streaming for the download
      const downloadResponse = await fetch(downloadUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });
      
      if (!downloadResponse.ok) {
        toast.error('Failed to download backup');
        return;
      }
      
      // Get the file size from headers
      const contentLength = downloadResponse.headers.get('content-length');
      const fileSizeMB = contentLength ? (parseInt(contentLength) / (1024 * 1024)).toFixed(2) : 'unknown';
      toast.info(`Downloading ${fileSizeMB} MB...`);
      
      // Stream the response to a blob
      const reader = downloadResponse.body.getReader();
      const chunks = [];
      let receivedLength = 0;
      
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        receivedLength += value.length;
        
        // Log progress for large files
        if (contentLength && receivedLength % (10 * 1024 * 1024) < 65536) { // Every ~10MB
          const progress = ((receivedLength / parseInt(contentLength)) * 100).toFixed(1);
          console.log(`Download progress: ${progress}%`);
        }
      }
      
      // Combine chunks into blob
      const blob = new Blob(chunks);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success(`Database downloaded successfully (${fileSizeMB} MB)`);
      fetchBackups();
    } catch (err) {
      console.error('Download error:', err);
      toast.error('Download failed: ' + (err.message || 'Unknown error'));
    }
  };
  
  // Poll for download backup status
  const pollDownloadBackupStatus = async (jobId) => {
    const maxAttempts = 120; // Poll for up to 10 minutes (5 sec intervals)
    let attempts = 0;
    
    const checkStatus = async () => {
      try {
        const response = await authFetch(`${API}/reports/backup-status/${jobId}`);
        if (!response.ok) {
          throw new Error('Failed to check backup status');
        }
        
        const data = await response.json();
        
        if (data.status === 'completed') {
          toast.success(`Backup completed! Starting download...`);
          setDownloadJobId(null);
          // Now trigger the download
          await triggerBackupDownload(data.filename);
          setDownloadingDatabase(false);
          return;
        } else if (data.status === 'failed') {
          toast.error(`Backup failed: ${data.error || 'Unknown error'}`);
          setDownloadingDatabase(false);
          setDownloadJobId(null);
          return;
        } else if (data.status === 'running') {
          // Still running, continue polling
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(checkStatus, 5000); // Poll every 5 seconds
          } else {
            toast.warning('Backup is taking longer than expected. Check backup list in a few minutes to download.');
            setDownloadingDatabase(false);
            setDownloadJobId(null);
          }
        } else {
          // Unknown status, refresh backup list
          toast.info('Backup status unknown. Refreshing backup list - you can download from there.');
          setDownloadingDatabase(false);
          setDownloadJobId(null);
          fetchBackups();
        }
      } catch (err) {
        console.error('Error polling backup status:', err);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(checkStatus, 5000);
        } else {
          toast.warning('Could not verify backup status. Check backup list manually to download.');
          setDownloadingDatabase(false);
          setDownloadJobId(null);
          fetchBackups();
        }
      }
    };
    
    checkStatus();
  };
  
  const downloadDatabase = async () => {
    setDownloadingDatabase(true);
    toast.info('Starting database backup for download... This runs in background and may take several minutes.');
    try {
      // First create the backup
      const createResponse = await authFetch(`${API}/reports/backup`, { 
        method: 'POST'
      });
      
      if (!createResponse.ok) {
        let errorMsg = 'Failed to start backup';
        try {
          const errorData = await createResponse.json();
          errorMsg = errorData.detail || errorMsg;
        } catch {
          errorMsg = `Failed to start backup (${createResponse.status})`;
        }
        toast.error(errorMsg);
        setDownloadingDatabase(false);
        return;
      }
      
      const backupData = await createResponse.json();
      
      if (backupData.job_id) {
        // Async backup - poll for completion
        setDownloadJobId(backupData.job_id);
        toast.info(`Backup started (Job ID: ${backupData.job_id}). Will download automatically when ready...`);
        pollDownloadBackupStatus(backupData.job_id);
      } else if (backupData.filename) {
        // Immediate completion (small DB) - download now
        await triggerBackupDownload(backupData.filename);
        setDownloadingDatabase(false);
      }
      
    } catch (err) {
      console.error('Download database exception:', err);
      toast.error('An error occurred: ' + (err.message || 'Network error - please check your connection'));
      setDownloadingDatabase(false);
    }
  };

  // Fetch backup error logs for debugging
  const fetchBackupErrorLogs = async () => {
    setLoadingBackupLogs(true);
    try {
      const response = await authFetch(`${API}/reports/backup-error-logs?limit=20`);
      if (response.ok) {
        const data = await response.json();
        setBackupErrorLogs(data.logs || []);
      } else {
        toast.error('Failed to fetch error logs');
      }
    } catch (err) {
      console.error('Error fetching backup logs:', err);
      toast.error('Error fetching backup logs');
    } finally {
      setLoadingBackupLogs(false);
    }
  };

  const downloadBackup = async (filename) => {
    try {
      const response = await authFetch(`${API}/reports/download-backup/${filename}`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        toast.error('Failed to download backup');
      }
    } catch (err) {
      console.error('Download error:', err);
      toast.error('Failed to download backup');
    }
  };

  const fetchSectors = async () => {
    try {
      const response = await authFetch(`${API}/sectors`);
      if (response.ok) setSectors(await response.json());
    } catch (err) {
      console.error('Failed to fetch sectors:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await authFetch(`${API}/admin/users`);
      if (response.ok) setUsers(await response.json());
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const handleSaveSector = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const url = editingSector 
        ? `${API}/sectors/${editingSector.sector_id}`
        : `${API}/sectors`;
      
      const response = await authJsonFetch(url, {
        method: editingSector ? 'PUT' : 'POST',
        body: JSON.stringify(sectorForm)
      });

      if (response.ok) {
        toast.success(editingSector ? 'Sector updated' : 'Sector created');
        setSectorDialogOpen(false);
        resetSectorForm();
        fetchSectors();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save sector');
      }
    } catch (err) {
      toast.error('An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSector = async (sectorId) => {
    if (!window.confirm('Are you sure you want to delete this sector?')) return;
    
    try {
      const response = await authFetch(`${API}/sectors/${sectorId}`, { method: 'DELETE' });

      if (response.ok) {
        toast.success('Sector deleted');
        fetchSectors();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete sector');
      }
    } catch (err) {
      toast.error('An error occurred');
    }
  };

  const openEditSector = (sector) => {
    setEditingSector(sector);
    setSectorForm({
      name: sector.name,
      display_name: sector.display_name,
      color: sector.color
    });
    setSectorDialogOpen(true);
  };

  const resetSectorForm = () => {
    setEditingSector(null);
    setSectorForm({ name: '', display_name: '', color: 'gray' });
  };

  const runStressTest = async () => {
    setStressTestLoading(true);
    try {
      const response = await authFetch(`${API}/admin/stress-test`, { method: 'POST' });

      if (response.ok) {
        const result = await response.json();
        toast.success(`Created ${result.total} test records`);
        setStressTestDialogOpen(false);
      } else {
        toast.error('Failed to run stress test');
      }
    } catch (err) {
      toast.error('An error occurred');
    } finally {
      setStressTestLoading(false);
    }
  };

  const clearStressTestData = async () => {
    setStressTestLoading(true);
    try {
      const response = await authFetch(`${API}/admin/stress-test`, { method: 'DELETE' });

      if (response.ok) {
        const result = await response.json();
        toast.success(`Deleted test records`);
      } else {
        toast.error('Failed to clear test data');
      }
    } catch (err) {
      toast.error('An error occurred');
    } finally {
      setStressTestLoading(false);
    }
  };

  // User management functions
  const deleteUser = async () => {
    if (!userToDelete) return;
    setUserActionLoading(true);
    try {
      const response = await authFetch(`${API}/admin/users/${userToDelete.user_id}`, { method: 'DELETE' });
      const data = await response.json();
      if (response.ok && data.success) {
        toast.success('User deleted');
        fetchUsers();
      } else {
        toast.error(data.detail || data.message || 'Failed to delete user');
      }
    } catch (err) {
      toast.error('An error occurred');
    } finally {
      setUserActionLoading(false);
      setUserToDelete(null);
    }
  };

  const updateUserRole = async (newRole) => {
    if (!userToUpdateRole) return;
    setUserActionLoading(true);
    try {
      const response = await authFetch(`${API}/admin/users/${userToUpdateRole.user_id}/role?role=${newRole}`, { method: 'PUT' });
      const data = await response.json();
      if (response.ok && data.success !== false) {
        toast.success('Role updated');
        fetchUsers();
      } else {
        toast.error(data.message || 'Failed to update role');
      }
    } catch (err) {
      toast.error('An error occurred');
    } finally {
      setUserActionLoading(false);
      setUserToUpdateRole(null);
    }
  };

  // Force-logout the target user across all devices.
  const forceLogoutUser = async () => {
    if (!userToForceLogout) return;
    setUserActionLoading(true);
    try {
      const response = await authFetch(
        `${API}/admin/users/${userToForceLogout.user_id}/force-logout`,
        { method: 'POST' }
      );
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.success !== false) {
        toast.success(
          data.message ||
            `Logged out ${userToForceLogout.name || userToForceLogout.email} from all devices`
        );
        fetchUsers();
      } else {
        toast.error(data.detail || data.message || 'Failed to force-logout user');
      }
    } catch (err) {
      toast.error('An error occurred');
    } finally {
      setUserActionLoading(false);
      setUserToForceLogout(null);
    }
  };

  // Database management functions
  const checkConsistency = async () => {
    setConsistencyLoading(true);
    try {
      const response = await authFetch(`${API}/admin/database/consistency`);
      if (response.ok) {
        const data = await response.json();
        setConsistencyReport(data);
        if (data.contacts_fixed_count > 0) {
          toast.success(`Consistency check complete: ${data.issues_count} issues found, ${data.contacts_fixed_count} contacts auto-fixed`);
        } else {
          toast.success(`Consistency check complete: ${data.issues_count} issues found`);
        }
      } else {
        toast.error('Failed to check consistency');
      }
    } catch (err) {
      toast.error('An error occurred');
    } finally {
      setConsistencyLoading(false);
    }
  };

  const enforcePrimaryContacts = async () => {
    setPrimaryEnforceLoading(true);
    setPrimaryEnforceResult(null);
    try {
      const response = await authJsonFetch(`${API}/admin/database/enforce-primary-contacts`, {
        method: 'POST'
      });
      if (response.ok) {
        const data = await response.json();
        setPrimaryEnforceResult(data);
        toast.success(`Primary contacts enforced: ${data.updated} updated, ${data.total_orgs_processed} orgs processed`);
      } else {
        toast.error('Failed to enforce primary contacts');
      }
    } catch (err) {
      toast.error('An error occurred');
    } finally {
      setPrimaryEnforceLoading(false);
    }
  };

  const fixOrphanOrgs = async () => {
    setOrphanOrgsLoading(true);
    setOrphanOrgsResult(null);
    try {
      const response = await authJsonFetch(`${API}/admin/database/fix-orphan-orgs`, {
        method: 'POST'
      });
      if (response.ok) {
        const data = await response.json();
        setOrphanOrgsResult(data);
        if (data.total_orphans === 0) {
          toast.success('No orphan organisations found');
        } else {
          toast.success(`${data.updated} organisation(s) reassigned to "Other" sector`);
        }
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to fix orphan organisations');
      }
    } catch (err) {
      toast.error('An error occurred while fixing orphan organisations');
    } finally {
      setOrphanOrgsLoading(false);
    }
  };  const fixOrphanContacts = async () => {
    setOrphanContactsLoading(true);
    setOrphanContactsResult(null);
    try {
      const response = await authJsonFetch(`${API}/admin/database/fix-orphan-contacts`, {
        method: 'POST'
      });
      if (response.ok) {
        const data = await response.json();
        setOrphanContactsResult(data);
        if (data.total_orphans === 0) {
          toast.success('No orphan contacts found');
        } else {
          toast.success(`${data.updated} contact(s) reassigned to "Unknown Organisation"`);
        }
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to fix orphan contacts');
      }
    } catch (err) {
      toast.error('An error occurred while fixing orphan contacts');
    } finally {
      setOrphanContactsLoading(false);
    }
  };

  // Scan all communication notes for references to OTHER organisation names
  // (potential mis-imports). Long-running so backend uses BackgroundTask + polling.
  const startCrossOrgNoteScan = async () => {
    setCrossOrgScanLoading(true);
    try {
      const startRes = await authJsonFetch(`${API}/admin/database/scan-cross-org-notes`, { method: 'POST' });
      if (!startRes.ok) {
        const err = await startRes.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to start scan');
        setCrossOrgScanLoading(false);
        return;
      }
      toast.info('Scan started — polling for results...');

      // Poll every 2s for up to 10 minutes
      const maxAttempts = 300;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        await new Promise(r => setTimeout(r, 2000));
        const res = await authFetch(`${API}/admin/database/scan-cross-org-notes/status`);
        if (!res.ok) continue;
        const data = await res.json();
        setCrossOrgScanStatus(data);
        if (data.status === 'completed') {
          toast.success(`Scan complete: ${data.match_count} potential cross-org references in ${data.scanned} notes`);
          break;
        }
        if (data.status === 'failed') {
          toast.error(`Scan failed: ${data.error || 'unknown error'}`);
          break;
        }
      }
    } catch (err) {
      toast.error('An error occurred while running scan');
    } finally {
      setCrossOrgScanLoading(false);
    }
  };  // Open the Clear Notes dialog (replaces the old window.confirm flow).
  // Inside the dialog the user can optionally narrow by date range and
  // preview the count before confirming.
  const openClearCommNotesDialog = () => {
    setClearNotesFromDate('');
    setClearNotesToDate('');
    setClearNotesPreview(null);
    setClearNotesConfirmText('');
    setClearNotesDialogOpen(true);
  };

  // ---- Date Normalization migration (DD-MMM-YYYY canonical) ----
  const openNormalizeDatesDialog = () => {
    setNormalizeDatesDry(null);
    setNormalizeDatesExecuted(null);
    setNormalizeDatesConfirm('');
    setNormalizeDatesOpen(true);
  };

  const runNormalizeDatesDry = async () => {
    setNormalizeDatesLoading(true);
    setNormalizeDatesDry(null);
    try {
      const res = await authJsonFetch(`${API}/database/normalize-dates`, {
        method: 'POST',
        body: JSON.stringify({ dry_run: true, sample_size: 8 }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Preview failed');
        return;
      }
      setNormalizeDatesDry(await res.json());
    } catch (_e) {
      toast.error('Preview failed');
    } finally {
      setNormalizeDatesLoading(false);
    }
  };

  const executeNormalizeDates = async () => {
    if (normalizeDatesConfirm !== 'NORMALIZE') {
      toast.error('Type NORMALIZE to confirm');
      return;
    }
    setNormalizeDatesLoading(true);
    setNormalizeDatesExecuted(null);
    try {
      const res = await authJsonFetch(`${API}/database/normalize-dates`, {
        method: 'POST',
        body: JSON.stringify({ dry_run: false, sample_size: 4 }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Normalization failed');
        return;
      }
      const result = await res.json();
      setNormalizeDatesExecuted(result);
      toast.success(`Normalized ${result.total_changed} values across the database`);
    } catch (_e) {
      toast.error('Normalization failed');
    } finally {
      setNormalizeDatesLoading(false);
    }
  };

  const previewClearCommNotes = async () => {
    setClearNotesPreviewLoading(true);
    setClearNotesPreview(null);
    try {
      const res = await authJsonFetch(`${API}/admin/database/clear-comm-notes/preview`, {
        method: 'POST',
        body: JSON.stringify({
          from_date: clearNotesFromDate || null,
          to_date: clearNotesToDate || null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Preview failed');
        return;
      }
      setClearNotesPreview(await res.json());
    } catch (_e) {
      toast.error('Preview failed');
    } finally {
      setClearNotesPreviewLoading(false);
    }
  };

  const clearCommNotes = async () => {
    if (clearNotesConfirmText !== 'DELETE') {
      toast.error('Type DELETE to confirm');
      return;
    }
    setClearNotesLoading(true);
    setClearNotesResult(null);
    try {
      const res = await authJsonFetch(`${API}/admin/database/clear-comm-notes`, {
        method: 'POST',
        body: JSON.stringify({
          from_date: clearNotesFromDate || null,
          to_date: clearNotesToDate || null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to clear notes');
        return;
      }
      const data = await res.json();
      setClearNotesResult(data);
      setClearNotesDialogOpen(false);
      toast.success(data.message || `Deleted ${data.deleted} note(s)`);
    } catch (_e) {
      toast.error('An error occurred while clearing notes');
    } finally {
      setClearNotesLoading(false);
    }
  };

  const exportCrossOrgScanCSV = () => {
    const matches = crossOrgScanStatus?.matches || [];
    if (matches.length === 0) {
      toast.error('No matches to export');
      return;
    }
    const escape = (v) => {
      const s = String(v ?? '').replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const headers = ['Note ID','Current Org','Matched Org (in note)','Matched Text','Note Timestamp','Imported From','Creator','Snippet'];
    const rows = matches.map(m => [
      m.note_id, m.current_org_name, m.matched_org_name, m.matched_text,
      m.note_timestamp, m.imported_from, m.creator, m.snippet
    ].map(escape).join(','));
    const csv = [headers.map(escape).join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cross-org-note-references_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };


  const downloadConsistencyReport = async () => {
    try {
      toast.info('Preparing consistency report...');
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API}/admin/consistency-check/download`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = 'consistency_report.xlsx';
        if (contentDisposition) {
          const match = contentDisposition.match(/filename=(.+)/);
          if (match) filename = match[1].replace(/['"]/g, '');
        }
        
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success('Report downloaded');
      } else {
        const err = await response.json();
        toast.error(err.detail || 'No issues to download');
      }
    } catch (err) {
      toast.error('Failed to download report');
    }
  };

  const deleteBackup = async () => {
    if (!backupToDelete) return;
    try {
      const response = await authFetch(`${API}/reports/backups/${backupToDelete}`, { method: 'DELETE' });
      if (response.ok) {
        toast.success('Backup deleted');
        fetchBackups();
      } else {
        toast.error('Failed to delete backup');
      }
    } catch (err) {
      toast.error('An error occurred');
    } finally {
      setBackupToDelete(null);
    }
  };

  const restoreDatabase = async () => {
    if (!restoreFile) return;
    setRestoreLoading(true);
    
    const formData = new FormData();
    formData.append('file', restoreFile);
    
    try {
      const response = await authFetch(
        `${API}/reports/restore?preserve_email_templates=${preserveEmailTemplates}&preserve_workflow_templates=${preserveWorkflowTemplates}&include_system_collections=${includeSystemCollections}`, 
        {
          method: 'POST',
          body: formData
        }
      );
      
      if (!response.ok) {
        let errorDetail = 'Restore failed';
        try {
          const data = await response.json();
          errorDetail = data.detail || errorDetail;
        } catch {
          // Response might not be JSON
        }
        toast.error(errorDetail);
        setRestoreLoading(false);
        return;
      }

      const enqueueData = await response.json();
      const restoreId = enqueueData.restore_id;
      if (!restoreId) {
        // Backwards-compat: server returned final result inline
        toast.success('Database restored successfully');
        setRestoreDialogOpen(false);
        setRestoreFile(null);
        setRestoreLoading(false);
        return;
      }

      toast.info(`Upload received (${enqueueData.total_upload_size_mb} MB). Restoring in background…`);

      // Poll for status. Restore can take many minutes for large files.
      // We also surface live progress (e.g. "Restoring users (24 MB, 1200 docs)…")
      // in the same toast so the operator can see WHY the restore is stuck
      // when it's slow. Previously the UI just showed a spinner and users
      // reported the feature as "not working" — it was working, but silent.
      let attempts = 0;
      let lastProgress = '';
      const MAX_ATTEMPTS = 720; // 720 * 5s = 60 minutes
      const poll = async () => {
        attempts += 1;
        try {
          const r = await authJsonFetch(`${API}/reports/restore/${restoreId}`);
          if (!r.ok) {
            toast.error('Lost connection to restore status');
            setRestoreLoading(false);
            return;
          }
          const status = await r.json();
          setRestoreProgress(status.progress || '');
          if (status.progress && status.progress !== lastProgress) {
            // Ephemeral progress toast (replaces the previous one so the
            // screen doesn't fill with 100 toasts on long restores).
            toast.info(status.progress, { id: 'restore-progress', duration: 6000 });
            lastProgress = status.progress;
          }
          if (status.status === 'completed') {
            toast.success('Database restored successfully', { id: 'restore-progress' });
            setRestoreDialogOpen(false);
            setRestoreFile(null);
            setRestoreLoading(false);
            setRestoreProgress('');
            return;
          }
          if (status.status === 'failed') {
            toast.error(`Restore failed: ${status.error || 'Unknown error'}`, { id: 'restore-progress' });
            setRestoreLoading(false);
            setRestoreProgress('');
            return;
          }
          if (attempts >= MAX_ATTEMPTS) {
            toast.error('Restore is taking longer than expected. Check status in Database Tools.');
            setRestoreLoading(false);
            return;
          }
          setTimeout(poll, 5000);
        } catch (e) {
          console.error('Restore poll error:', e);
          if (attempts < MAX_ATTEMPTS) {
            setTimeout(poll, 5000);
          } else {
            setRestoreLoading(false);
          }
        }
      };
      setTimeout(poll, 3000);
    } catch (err) {
      console.error('Restore error:', err);
      toast.error('An error occurred during restore');
      setRestoreLoading(false);
    }
  };

  const clearAllData = async () => {
    setClearDataLoading(true);
    try {
      const response = await authFetch(
        `${API}/admin/clear-all-data?preserve_email_templates=${preserveEmailTemplates}&preserve_workflow_templates=${preserveWorkflowTemplates}`, 
        { method: 'DELETE' }
      );
      
      if (response.ok) {
        const data = await response.json();
        toast.success('All data cleared successfully');
        setClearAllDataDialog(false);
      } else {
        let errorMsg = 'Failed to clear data';
        try {
          const errorData = await response.json();
          errorMsg = errorData.detail || errorMsg;
        } catch {
          errorMsg = `Failed to clear data (${response.status})`;
        }
        toast.error(errorMsg);
        console.error('Clear data error:', response.status, errorMsg);
      }
    } catch (err) {
      console.error('Clear data exception:', err);
      toast.error('An error occurred: ' + (err.message || 'Unknown error'));
    } finally {
      setClearDataLoading(false);
    }
  };

  // XLS Export function
  const exportDatabaseToXLS = async () => {
    setXlsExportLoading(true);
    try {
      const token = localStorage.getItem('gs_session_token');
      const response = await fetch(`${API}/database/export-xls`, {
        method: 'GET',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `database_export_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        // Delay cleanup to ensure download starts
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
          a.remove();
        }, 100);
        toast.success('Database exported - check your downloads');
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to export database');
      }
    } catch (err) {
      console.error('Export error:', err);
      toast.error('An error occurred during export');
    } finally {
      setXlsExportLoading(false);
    }
  };

  // XLS Upload for verification
  const handleXlsUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setXlsUploadLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const token = localStorage.getItem('gs_session_token');
      
      // Upload file and fetch DB fields in parallel
      const [uploadResponse, fieldsResponse] = await Promise.all([
        fetch(`${API}/database/upload-xls`, {
          method: 'POST',
          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
          body: formData
        }),
        authFetch(`${API}/database/organization-fields`)
      ]);
      
      if (uploadResponse.ok) {
        const data = await uploadResponse.json();
        console.log('Upload response:', data);
        setXlsPreviewData(data);
        setXlsSelectedSheets([]);
        setSelectedImportSheet('');
        setFieldMappings({});
        setSmartImportStep(2);
        toast.success('File uploaded. Select a sheet to import.');
        
        // Load DB fields
        if (fieldsResponse.ok) {
          const fieldsData = await fieldsResponse.json();
          console.log('DB fields loaded:', fieldsData.fields?.length);
          setDbFields(fieldsData.fields || []);
        } else {
          console.error('Failed to load DB fields');
        }
      } else {
        const err = await uploadResponse.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to upload file');
      }
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('An error occurred during upload');
    } finally {
      setXlsUploadLoading(false);
      if (e.target) e.target.value = '';
    }
  };

  // XLS Import to database
  const importXlsToDatabase = async () => {
    if (!xlsPreviewData?.temp_filename) return;
    
    setXlsImportLoading(true);
    try {
      const response = await authJsonFetch(`${API}/database/import-xls`, {
        method: 'POST',
        body: JSON.stringify({
          temp_filename: xlsPreviewData.temp_filename,
          sheets_to_import: xlsSelectedSheets,
          mode: xlsImportMode
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Import completed: ${Object.keys(data.results.imported).length} collections imported`);
        setXlsPreviewData(null);
        setXlsImportDialogOpen(false);
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to import data');
      }
    } catch (err) {
      toast.error('An error occurred during import');
    } finally {
      setXlsImportLoading(false);
    }
  };

  // Cancel XLS import
  const cancelXlsImport = async () => {
    if (xlsPreviewData?.temp_filename) {
      try {
        await authFetch(`${API}/database/cancel-import/${xlsPreviewData.temp_filename}`, { method: 'DELETE' });
      } catch (e) {
        console.error('Cancel cleanup error:', e);
      }
    }
    setXlsPreviewData(null);
    setXlsImportDialogOpen(false);
    setSmartImportStep(1);
    setSelectedImportSheet('');
    setFieldMappings({});
    setImportResults(null);
    setDbFields([]);
  };

  // Smart Import with field mapping
  const executeSmartImport = async () => {
    if (!xlsPreviewData?.temp_filename || !selectedImportSheet) return;
    
    // Convert field mappings object to array format
    const mappingsArray = Object.entries(fieldMappings)
      .filter(([_, dbField]) => dbField)
      .map(([xlsCol, dbField]) => ({
        xls_column: xlsCol,
        db_field: dbField
      }));
    
    if (mappingsArray.length === 0) {
      toast.error('Please map at least one field');
      return;
    }
    
    // Check if match field is mapped
    const hasMatchField = mappingsArray.some(m => m.db_field === matchField);
    if (!hasMatchField) {
      toast.error(`Please map a column to "${matchField}" (used for matching)`);
      return;
    }
    
    setXlsImportLoading(true);
    try {
      const response = await authJsonFetch(`${API}/database/smart-import`, {
        method: 'POST',
        body: JSON.stringify({
          temp_filename: xlsPreviewData.temp_filename,
          sheet_name: selectedImportSheet,
          field_mappings: mappingsArray,
          match_field: matchField,
          update_existing: updateExisting,
          create_new: createNew
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setImportResults(data.results);
        setSmartImportStep(4);
        toast.success(data.message);
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Import failed');
      }
    } catch (err) {
      toast.error('An error occurred during import');
    } finally {
      setXlsImportLoading(false);
    }
  };

  // Auto-detect field mappings based on column names
  const autoMapFields = () => {
    if (!xlsPreviewData || !selectedImportSheet || !dbFields.length) return;
    
    const sheet = xlsPreviewData.sheets[selectedImportSheet];
    if (!sheet) return;
    
    const newMappings = {};
    const xlsColumns = sheet.columns || [];
    
    // Common name variations to match
    const fieldAliases = {
      'org_name': ['org_name', 'organisation', 'organization', 'name', 'company', 'school', 'org name', 'organisation name'],
      'contact_name': ['contact_name', 'contact', 'contact person', 'primary contact', 'contact name'],
      'contact_email': ['contact_email', 'email', 'e-mail', 'contact email', 'primary email'],
      'contact_phone': ['contact_phone', 'phone', 'telephone', 'mobile', 'contact phone'],
      'address': ['address', 'street', 'street address', 'address line 1'],
      'city': ['city', 'suburb', 'town'],
      'state': ['state', 'state/territory', 'region'],
      'postcode': ['postcode', 'post code', 'zip', 'zip code', 'postal code'],
      'sector_id': ['sector_id', 'sector', 'category', 'type'],
      'year': ['year', 'school year', 'financial year'],
      'notes': ['notes', 'comments', 'description'],
      'abn': ['abn', 'tax id', 'business number'],
      'website': ['website', 'web', 'url', 'site']
    };
    
    for (const xlsCol of xlsColumns) {
      const xlsColLower = xlsCol.toLowerCase().trim();
      
      for (const [dbField, aliases] of Object.entries(fieldAliases)) {
        if (aliases.some(alias => xlsColLower === alias.toLowerCase() || xlsColLower.includes(alias.toLowerCase()))) {
          newMappings[xlsCol] = dbField;
          break;
        }
      }
    }
    
    setFieldMappings(newMappings);
    toast.success(`Auto-mapped ${Object.keys(newMappings).length} fields`);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    try {
      return new Date(dateStr).toLocaleString('en-AU', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Australia/Sydney'
      });
    } catch {
      return 'N/A';
    }
  };

  // Compact relative time for the Last Active column: "2m ago", "5h ago", etc.
  // Falls back to a Sydney-zone absolute date once older than ~30 days.
  const formatRelativeTime = (dateStr) => {
    if (!dateStr) return 'Never';
    try {
      const then = new Date(dateStr).getTime();
      if (Number.isNaN(then)) return 'N/A';
      const diffSec = Math.round((Date.now() - then) / 1000);
      if (diffSec < 30) return 'just now';
      if (diffSec < 60) return `${diffSec}s ago`;
      const diffMin = Math.round(diffSec / 60);
      if (diffMin < 60) return `${diffMin}m ago`;
      const diffHr = Math.round(diffMin / 60);
      if (diffHr < 24) return `${diffHr}h ago`;
      const diffDay = Math.round(diffHr / 24);
      if (diffDay < 30) return `${diffDay}d ago`;
      return formatDate(dateStr);
    } catch {
      return 'N/A';
    }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const colorOptions = [
    { value: 'blue', label: 'Blue', className: 'bg-blue-500' },
    { value: 'green', label: 'Green', className: 'bg-green-500' },
    { value: 'purple', label: 'Purple', className: 'bg-purple-500' },
    { value: 'red', label: 'Red', className: 'bg-red-500' },
    { value: 'yellow', label: 'Yellow', className: 'bg-yellow-500' },
    { value: 'gray', label: 'Gray', className: 'bg-gray-500' },
  ];

  return (
    <div data-testid="settings-page" className="space-y-6 max-w-5xl">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground mt-1">
          Manage your account and system preferences
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="appearance">
            <Palette className="w-4 h-4 mr-2" />
            Appearance
          </TabsTrigger>
          <TabsTrigger value="profile">
            <User className="w-4 h-4 mr-2" />
            Profile
          </TabsTrigger>
          {isAdmin && (
            <>
              <TabsTrigger value="sectors">
                <Folder className="w-4 h-4 mr-2" />
                Sectors
              </TabsTrigger>
              <TabsTrigger value="users">
                <Users className="w-4 h-4 mr-2" />
                Users
              </TabsTrigger>
              <TabsTrigger value="database">
                <Database className="w-4 h-4 mr-2" />
                Database
              </TabsTrigger>
              <TabsTrigger value="calendar">
                <Calendar className="w-4 h-4 mr-2" />
                Calendar
              </TabsTrigger>
              <TabsTrigger value="reports">
                <FileDown className="w-4 h-4 mr-2" />
                Reports
              </TabsTrigger>
              <TabsTrigger value="ai-provider">
                <Activity className="w-4 h-4 mr-2" />
                AI Provider
              </TabsTrigger>
              <TabsTrigger value="error-logs" data-testid="tab-error-logs">
                <AlertTriangle className="w-4 h-4 mr-2" />
                Error Logs
              </TabsTrigger>
              <TabsTrigger value="saved-search-audit" data-testid="tab-saved-search-audit">
                <Archive className="w-4 h-4 mr-2" />
                Saved Search Recovery
              </TabsTrigger>
              <TabsTrigger value="feature-flags" data-testid="tab-feature-flags">
                <Sparkles className="w-4 h-4 mr-2" />
                Feature Flags
              </TabsTrigger>
            </>
          )}
        </TabsList>

        {/* Appearance Tab */}
        <TabsContent value="appearance" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="w-5 h-5 text-primary" />
                Theme
              </CardTitle>
              <CardDescription>
                Customize the look and feel of the application. Your preference is saved automatically.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Royal Blue */}
                <div 
                  className={cn(
                    "p-4 rounded-lg border-2 cursor-pointer transition-all",
                    theme === 'royal-blue' ? "border-blue-500 ring-2 ring-blue-500/20" : "border-transparent hover:border-gray-300"
                  )}
                  onClick={() => setTheme('royal-blue')}
                  style={{ backgroundColor: '#f8fafc' }}
                  data-testid="theme-royal-blue"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <Crown className="w-5 h-5 text-blue-600" />
                    <span className="font-semibold text-slate-900">Royal Blue</span>
                  </div>
                  <p className="text-sm text-slate-600 mb-3">Professional and corporate aesthetic</p>
                  <div className="flex gap-2">
                    <div className="w-8 h-8 rounded-full bg-blue-600" />
                    <div className="w-8 h-8 rounded-full bg-white border" />
                    <div className="w-8 h-8 rounded-full bg-slate-100" />
                  </div>
                </div>

                {/* Barbie Pink */}
                <div 
                  className={cn(
                    "p-4 rounded-lg border-2 cursor-pointer transition-all",
                    theme === 'barbie-pink' ? "border-pink-500 ring-2 ring-pink-500/20" : "border-transparent hover:border-gray-300"
                  )}
                  onClick={() => setTheme('barbie-pink')}
                  style={{ backgroundColor: '#fdf2f8' }}
                  data-testid="theme-barbie-pink"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <Crown className="w-5 h-5 text-pink-500" />
                    <span className="font-semibold text-pink-900">Barbie Pink</span>
                  </div>
                  <p className="text-sm text-pink-700 mb-3">Light with pink essence</p>
                  <div className="flex gap-2">
                    <div className="w-8 h-8 rounded-full bg-pink-500" />
                    <div className="w-8 h-8 rounded-full" style={{ backgroundColor: '#fdf2f8' }} />
                    <div className="w-8 h-8 rounded-full bg-pink-200" />
                  </div>
                </div>

                {/* Caramel Indulgence */}
                <div 
                  className={cn(
                    "p-4 rounded-lg border-2 cursor-pointer transition-all",
                    theme === 'caramel-indulgence' ? "border-amber-600 ring-2 ring-amber-500/20" : "border-transparent hover:border-gray-300"
                  )}
                  onClick={() => setTheme('caramel-indulgence')}
                  style={{ backgroundColor: '#fffbeb' }}
                  data-testid="theme-caramel-indulgence"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <Crown className="w-5 h-5 text-amber-700" />
                    <span className="font-semibold text-amber-900">Caramel Indulgence</span>
                  </div>
                  <p className="text-sm text-amber-700 mb-3">Warm caramel tones</p>
                  <div className="flex gap-2">
                    <div className="w-8 h-8 rounded-full bg-amber-600" />
                    <div className="w-8 h-8 rounded-full" style={{ backgroundColor: '#fef3c7' }} />
                    <div className="w-8 h-8 rounded-full bg-amber-200" />
                  </div>
                </div>

                {/* Ocean Depths */}
                <div 
                  className={cn(
                    "p-4 rounded-lg border-2 cursor-pointer transition-all",
                    theme === 'ocean-depths' ? "border-teal-500 ring-2 ring-teal-500/20" : "border-transparent hover:border-gray-300"
                  )}
                  onClick={() => setTheme('ocean-depths')}
                  style={{ backgroundColor: '#f0fdfa' }}
                  data-testid="theme-ocean-depths"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <Crown className="w-5 h-5 text-teal-600" />
                    <span className="font-semibold text-teal-900">Ocean Depths</span>
                  </div>
                  <p className="text-sm text-teal-700 mb-3">Deep sea blues and teals</p>
                  <div className="flex gap-2">
                    <div className="w-8 h-8 rounded-full bg-teal-600" />
                    <div className="w-8 h-8 rounded-full" style={{ backgroundColor: '#f0fdfa' }} />
                    <div className="w-8 h-8 rounded-full bg-teal-200" />
                  </div>
                </div>

                {/* Regal Garden */}
                <div 
                  className={cn(
                    "p-4 rounded-lg border-2 cursor-pointer transition-all",
                    theme === 'regal-garden' ? "border-violet-500 ring-2 ring-violet-500/20" : "border-transparent hover:border-gray-300"
                  )}
                  onClick={() => setTheme('regal-garden')}
                  style={{ backgroundColor: '#f5f3ff' }}
                  data-testid="theme-regal-garden"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <Crown className="w-5 h-5 text-violet-600" />
                    <span className="font-semibold text-violet-900">Regal Garden</span>
                  </div>
                  <p className="text-sm text-violet-700 mb-3">Rich greens and purples</p>
                  <div className="flex gap-2">
                    <div className="w-8 h-8 rounded-full bg-violet-600" />
                    <div className="w-8 h-8 rounded-full" style={{ backgroundColor: '#f5f3ff' }} />
                    <div className="w-8 h-8 rounded-full bg-violet-200" />
                  </div>
                </div>

                {/* Warm Neutrals */}
                <div 
                  className={cn(
                    "p-4 rounded-lg border-2 cursor-pointer transition-all",
                    theme === 'warm-neutrals' ? "border-stone-500 ring-2 ring-stone-500/20" : "border-transparent hover:border-gray-300"
                  )}
                  onClick={() => setTheme('warm-neutrals')}
                  style={{ backgroundColor: '#fafaf9' }}
                  data-testid="theme-warm-neutrals"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <Crown className="w-5 h-5 text-stone-600" />
                    <span className="font-semibold text-stone-800">Warm Neutrals</span>
                  </div>
                  <p className="text-sm text-stone-600 mb-3">Soft and cozy earth tones</p>
                  <div className="flex gap-2">
                    <div className="w-8 h-8 rounded-full bg-stone-500" />
                    <div className="w-8 h-8 rounded-full" style={{ backgroundColor: '#fafaf9' }} />
                    <div className="w-8 h-8 rounded-full bg-stone-200" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Profile Tab */}
        <TabsContent value="profile" className="mt-6 space-y-6">
          <Card className={cn(isBarbiePink && "card-brutalist")}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                Profile
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-6">
                <Avatar className="w-20 h-20">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback className="text-2xl">{getInitials(user?.name)}</AvatarFallback>
                </Avatar>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-muted-foreground" />
                    <span className="font-medium">{user?.name}</span>
                    <Badge variant={user?.role === 'admin' ? 'default' : 'secondary'}>
                      {user?.role}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">{user?.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      Authenticated via Google OAuth
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Gmail Integration */}
          <Card className={cn(isBarbiePink && "card-brutalist")}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-primary" />
                Gmail & Google Drive Integration
              </CardTitle>
              <CardDescription>
                Connect your Google account to send emails and store files in the shared Google Drive
              </CardDescription>
            </CardHeader>
            <CardContent>
              {gmailStatus?.connected ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-900">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                    <div className="flex-1">
                      <p className="font-medium text-green-800 dark:text-green-200">Gmail Connected</p>
                      <p className="text-sm text-green-600 dark:text-green-400">{gmailStatus.gmail_email}</p>
                    </div>
                  </div>
                  
                  {/* Google Drive Status */}
                  {gmailStatus?.drive_connected ? (
                    <div className="flex items-center gap-3 p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-900">
                      <Cloud className="w-6 h-6 text-blue-600" />
                      <div className="flex-1">
                        <p className="font-medium text-blue-800 dark:text-blue-200">Google Drive Connected</p>
                        <p className="text-sm text-blue-600 dark:text-blue-400">
                          Files will be stored in the shared team drive
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 p-4 bg-yellow-50 dark:bg-yellow-950/20 rounded-lg border border-yellow-200 dark:border-yellow-900">
                      <AlertTriangle className="w-6 h-6 text-yellow-600" />
                      <div className="flex-1">
                        <p className="font-medium text-yellow-800 dark:text-yellow-200">Google Drive Not Connected</p>
                        <p className="text-sm text-yellow-600 dark:text-yellow-400">
                          Reconnect to enable file storage in shared drive
                        </p>
                      </div>
                      <Button 
                        variant="outline"
                        size="sm"
                        onClick={connectGmail}
                        disabled={gmailLoading}
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Reconnect
                      </Button>
                    </div>
                  )}
                  
                  <div className="flex gap-3">
                    <Button 
                      variant="outline" 
                      onClick={sendTestEmail}
                      disabled={gmailLoading}
                      data-testid="gmail-test-btn"
                    >
                      {gmailLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4 mr-2" />
                      )}
                      Send Test Email
                    </Button>
                    <Button 
                      variant="destructive" 
                      onClick={disconnectGmail}
                      disabled={gmailLoading}
                      data-testid="gmail-disconnect-btn"
                    >
                      {gmailLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <XCircle className="w-4 h-4 mr-2" />
                      )}
                      Disconnect Google Services
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 bg-yellow-50 dark:bg-yellow-950/20 rounded-lg border border-yellow-200 dark:border-yellow-900">
                    <AlertTriangle className="w-6 h-6 text-yellow-600" />
                    <div className="flex-1">
                      <p className="font-medium text-yellow-800 dark:text-yellow-200">Google Account Not Connected</p>
                      <p className="text-sm text-yellow-600 dark:text-yellow-400">
                        Connect your Google account to send emails and store files in the shared drive
                      </p>
                    </div>
                  </div>
                  <Button 
                    onClick={connectGmail}
                    disabled={gmailLoading}
                    data-testid="gmail-connect-btn"
                  >
                    {gmailLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Mail className="w-4 h-4 mr-2" />
                    )}
                    Connect Google Account
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sectors Tab (Admin Only) */}
        {isAdmin && (
          <TabsContent value="sectors" className="mt-6">
            <Card className={cn(isBarbiePink && "card-brutalist")}>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Sector Management</CardTitle>
                  <CardDescription>Add, edit, or remove organization sectors</CardDescription>
                </div>
                <Button onClick={() => { resetSectorForm(); setSectorDialogOpen(true); }}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Sector
                </Button>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Display Name</TableHead>
                      <TableHead>Color</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sectors.map(sector => (
                      <TableRow key={sector.sector_id}>
                        <TableCell className="font-mono">{sector.name}</TableCell>
                        <TableCell>{sector.display_name}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className={cn("w-4 h-4 rounded-full", `bg-${sector.color}-500`)} />
                            {sector.color}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="icon" onClick={() => openEditSector(sector)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDeleteSector(sector.sector_id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Sector Dialog */}
            <Dialog open={sectorDialogOpen} onOpenChange={setSectorDialogOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingSector ? 'Edit Sector' : 'Add Sector'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSaveSector}>
                  <div className="space-y-4 py-4">
                    <div>
                      <Label>Internal Name *</Label>
                      <Input
                        value={sectorForm.name}
                        onChange={(e) => setSectorForm({...sectorForm, name: e.target.value.toLowerCase().replace(/\s+/g, '_')})}
                        placeholder="e.g., private_school"
                        required
                      />
                    </div>
                    <div>
                      <Label>Display Name *</Label>
                      <Input
                        value={sectorForm.display_name}
                        onChange={(e) => setSectorForm({...sectorForm, display_name: e.target.value})}
                        placeholder="e.g., Private School"
                        required
                      />
                    </div>
                    <div>
                      <Label>Color</Label>
                      <div className="flex gap-2 mt-2">
                        {colorOptions.map(color => (
                          <button
                            key={color.value}
                            type="button"
                            className={cn(
                              "w-8 h-8 rounded-full transition-all",
                              color.className,
                              sectorForm.color === color.value && "ring-2 ring-offset-2 ring-primary"
                            )}
                            onClick={() => setSectorForm({...sectorForm, color: color.value})}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setSectorDialogOpen(false)}>Cancel</Button>
                    <Button type="submit" disabled={loading}>
                      {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      {editingSector ? 'Update' : 'Create'}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </TabsContent>
        )}

        {/* Users Tab (Admin Only) */}
        {isAdmin && (
          <TabsContent value="users" className="mt-6">
            <Card className={cn(isBarbiePink && "card-brutalist")}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-primary" />
                  User Management
                </CardTitle>
                <CardDescription>View, manage roles, and remove system users</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Last Active</TableHead>
                      <TableHead>Last Login</TableHead>
                      <TableHead>Joined</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map(u => (
                      <TableRow key={u.user_id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Avatar className="w-8 h-8">
                              <AvatarImage src={u.picture} />
                              <AvatarFallback>{getInitials(u.name)}</AvatarFallback>
                            </Avatar>
                            <span className="font-medium">{u.name}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">{u.email}</TableCell>
                        <TableCell>
                          <Badge variant={u.role === 'admin' ? 'default' : 'secondary'} className="gap-1">
                            {u.role === 'admin' ? <Shield className="w-3 h-3" /> : <User className="w-3 h-3" />}
                            {u.role}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">
                          {u.last_active_at ? (
                            <div className="flex flex-col">
                              <span>{formatRelativeTime(u.last_active_at)}</span>
                              {u.active_session_count > 0 && (
                                <span className="text-xs text-muted-foreground">
                                  {u.active_session_count} active {u.active_session_count === 1 ? 'session' : 'sessions'}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDate(u.last_login_at)}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDate(u.created_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setUserToUpdateRole(u)}
                              disabled={u.user_id === user?.user_id}
                              title="Change role"
                              data-testid={`change-role-${u.user_id}`}
                            >
                              <UserCog className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-amber-600 hover:bg-amber-500 hover:text-white border-amber-300"
                              onClick={() => setUserToForceLogout(u)}
                              disabled={u.user_id === user?.user_id || (u.active_session_count || 0) === 0}
                              title={
                                u.user_id === user?.user_id
                                  ? "Can't force-logout yourself"
                                  : (u.active_session_count || 0) === 0
                                  ? "No active sessions to log out"
                                  : "Force logout from all devices"
                              }
                              data-testid={`force-logout-${u.user_id}`}
                            >
                              <LogOut className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-destructive hover:bg-destructive hover:text-white"
                              onClick={() => setUserToDelete(u)}
                              disabled={u.user_id === user?.user_id}
                              title="Delete user"
                              data-testid={`delete-user-${u.user_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Delete User Confirmation */}
            <AlertDialog open={!!userToDelete} onOpenChange={() => setUserToDelete(null)}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle className="flex items-center gap-2 text-destructive">
                    <AlertTriangle className="w-5 h-5" />
                    Delete User
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete <strong>{userToDelete?.name}</strong> ({userToDelete?.email})?
                    This will remove their account and all sessions.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={deleteUser} 
                    className="bg-destructive hover:bg-destructive/90"
                    disabled={userActionLoading}
                  >
                    {userActionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Delete User
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            {/* Update Role Dialog */}
            <AlertDialog open={!!userToUpdateRole} onOpenChange={() => setUserToUpdateRole(null)}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Change User Role</AlertDialogTitle>
                  <AlertDialogDescription>
                    Update role for <strong>{userToUpdateRole?.name}</strong>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <div className="py-4 space-y-2">
                  <Button
                    variant={userToUpdateRole?.role === 'admin' ? 'default' : 'outline'}
                    className="w-full justify-start"
                    onClick={() => updateUserRole('admin')}
                    disabled={userActionLoading}
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    Admin - Full system access
                  </Button>
                  <Button
                    variant={userToUpdateRole?.role === 'standard' ? 'default' : 'outline'}
                    className="w-full justify-start"
                    onClick={() => updateUserRole('standard')}
                    disabled={userActionLoading}
                  >
                    <User className="w-4 h-4 mr-2" />
                    Standard - Limited access
                  </Button>
                </div>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            {/* Force Logout Confirmation */}
            <AlertDialog open={!!userToForceLogout} onOpenChange={() => setUserToForceLogout(null)}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
                    <LogOut className="w-5 h-5" />
                    Force Logout User
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    Sign <strong>{userToForceLogout?.name}</strong> ({userToForceLogout?.email}) out of all devices?
                    {(userToForceLogout?.active_session_count || 0) > 0 && (
                      <>
                        {' '}They currently have{' '}
                        <strong>{userToForceLogout.active_session_count}</strong> active{' '}
                        {userToForceLogout.active_session_count === 1 ? 'session' : 'sessions'}.
                      </>
                    )}
                    {' '}They will need to sign in again with Google. Their account, role and data are unchanged.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={forceLogoutUser}
                    disabled={userActionLoading}
                    className="bg-amber-600 hover:bg-amber-700"
                    data-testid="confirm-force-logout"
                  >
                    {userActionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Force Logout
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </TabsContent>
        )}

        {/* Database Tab (Admin Only) */}
        {isAdmin && (
          <TabsContent value="database" className="mt-6 space-y-6">
            {/* Database Health Card (collapsible — default collapsed) */}
            <Card className={cn(isBarbiePink && "card-brutalist")}>
              <CardHeader
                className="cursor-pointer select-none hover:bg-muted/40 transition-colors"
                onClick={() => setDbHealthExpanded((v) => !v)}
                data-testid="dbhealth-toggle"
              >
                <CardTitle className="flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-primary" />
                  Database Health
                  <span className="ml-auto text-xs font-normal text-muted-foreground">
                    {dbHealthExpanded ? 'Click to collapse' : 'Click to expand'}
                  </span>
                  {dbHealthExpanded ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </CardTitle>
                <CardDescription>Check data consistency and integrity</CardDescription>
              </CardHeader>
              {dbHealthExpanded && (
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Consistency Check</p>
                    <p className="text-sm text-muted-foreground">
                      Scan for orphaned records, invalid references, and auto-fix contact primary status
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={checkConsistency} disabled={consistencyLoading}>
                      {consistencyLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Activity className="w-4 h-4 mr-2" />
                      )}
                      Run Check
                    </Button>
                    {consistencyReport && (consistencyReport.issues_count > 0 || consistencyReport.contacts_fixed_count > 0) && (
                      <Button variant="outline" onClick={downloadConsistencyReport}>
                        <Download className="w-4 h-4 mr-2" />
                        Download Report
                      </Button>
                    )}
                  </div>
                </div>

                {consistencyReport && (
                  <div className={cn(
                    "p-4 rounded-lg border",
                    consistencyReport.status === 'healthy' 
                      ? "bg-green-50 dark:bg-green-950/30 border-green-200"
                      : "bg-amber-50 dark:bg-amber-950/30 border-amber-200"
                  )}>
                    <div className="flex items-center gap-2 mb-3">
                      {consistencyReport.status === 'healthy' ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <AlertTriangle className="w-5 h-5 text-amber-600" />
                      )}
                      <span className="font-semibold">
                        {consistencyReport.status === 'healthy' ? 'Database Healthy' : `${consistencyReport.issues_count} Issues Found`}
                      </span>
                      {consistencyReport.contacts_fixed_count > 0 && (
                        <Badge variant="default" className="ml-2 bg-blue-500">
                          {consistencyReport.contacts_fixed_count} contacts auto-fixed
                        </Badge>
                      )}
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-4 gap-2 mb-3">
                      {Object.entries(consistencyReport.stats || {}).map(([key, value]) => (
                        <div key={key} className="text-center p-2 bg-background rounded">
                          <p className="text-lg font-bold">{value}</p>
                          <p className="text-xs text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</p>
                        </div>
                      ))}
                    </div>

                    {/* Issues */}
                    {consistencyReport.issues?.length > 0 && (
                      <div className="space-y-3">
                        <h4 className="font-medium text-sm">Issues & Fixes:</h4>
                        {consistencyReport.issues.map((issue, idx) => (
                          <details key={idx} className="text-sm bg-background rounded border">
                            <summary className="p-2 cursor-pointer hover:bg-muted/50 flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Badge variant={
                                  issue.severity === 'warning' ? 'default' : 
                                  issue.severity === 'info' ? 'secondary' : 
                                  'outline'
                                } className={
                                  issue.severity === 'warning' ? 'bg-amber-500' :
                                  issue.severity === 'info' ? 'bg-blue-500' : ''
                                }>
                                  {issue.severity}
                                </Badge>
                                <span className="font-medium">{issue.type}</span>
                                <span className="text-muted-foreground">({issue.count} records)</span>
                              </div>
                            </summary>
                            <div className="p-3 border-t space-y-2">
                              <p className="text-muted-foreground">{issue.description}</p>
                              
                              {/* Show samples for regular issues */}
                              {issue.samples && issue.type !== 'contacts_primary_fixed' && (
                                <div className="mt-2">
                                  <p className="text-xs font-medium mb-1">Sample records:</p>
                                  <div className="max-h-40 overflow-y-auto bg-muted/50 rounded p-2">
                                    <pre className="text-xs whitespace-pre-wrap">
                                      {JSON.stringify(issue.samples, null, 2)}
                                    </pre>
                                  </div>
                                </div>
                              )}
                              
                              {/* Show fixes applied for contact fixes */}
                              {issue.type === 'contacts_primary_fixed' && issue.samples && (
                                <div className="mt-2">
                                  <p className="text-xs font-medium mb-1">Fixes applied:</p>
                                  <div className="max-h-60 overflow-y-auto space-y-1">
                                    {issue.samples.map((fix, fIdx) => (
                                      <div key={fIdx} className="text-xs p-2 bg-muted/50 rounded flex justify-between items-center">
                                        <span>
                                          <strong>{fix.contact_name}</strong> at {fix.org_name}
                                        </span>
                                        <Badge variant="outline" className="text-xs">
                                          {fix.action}
                                        </Badge>
                                      </div>
                                    ))}
                                    {issue.all_fixes && issue.all_fixes.length > 10 && (
                                      <p className="text-xs text-muted-foreground text-center py-1">
                                        ... and {issue.all_fixes.length - 10} more (download report for full list)
                                      </p>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </details>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Primary Contact Enforce */}
                <div className="flex items-center justify-between pt-2 border-t">
                  <div>
                    <p className="font-medium">Primary Contact Enforce</p>
                    <p className="text-sm text-muted-foreground">
                      1 contact → set as primary. 2 contacts → primary is the one whose name differs from organisation name.
                    </p>
                  </div>
                  <Button 
                    onClick={enforcePrimaryContacts} 
                    disabled={primaryEnforceLoading}
                    variant="outline"
                    data-testid="enforce-primary-contacts-btn"
                  >
                    {primaryEnforceLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Users className="w-4 h-4 mr-2" />
                    )}
                    Enforce Primary
                  </Button>
                </div>
                {primaryEnforceResult && (
                  <div className="p-3 rounded-lg border bg-blue-50 dark:bg-blue-950/30 border-blue-200">
                    <div className="flex items-center gap-2 mb-1">
                      <CheckCircle className="w-4 h-4 text-blue-600" />
                      <span className="font-medium text-sm">
                        {primaryEnforceResult.updated} contacts updated across {primaryEnforceResult.total_orgs_processed} organisations
                      </span>
                    </div>
                    {primaryEnforceResult.details_sample?.length > 0 && (
                      <div className="mt-2 max-h-32 overflow-y-auto space-y-1">
                        {primaryEnforceResult.details_sample.map((d, i) => (
                          <div key={i} className="text-xs text-muted-foreground">
                            {d.contact} — {d.action.replace(/_/g, ' ')}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Fix Orphan Organisations */}
                <div className="flex items-center justify-between pt-2 border-t">
                  <div>
                    <p className="font-medium">Fix Orphan Organisations</p>
                    <p className="text-sm text-muted-foreground">
                      Assign organisations with no sector (or invalid sector) to the "Other" sector.
                    </p>
                  </div>
                  <Button
                    onClick={fixOrphanOrgs}
                    disabled={orphanOrgsLoading}
                    variant="outline"
                    data-testid="fix-orphan-orgs-btn"
                  >
                    {orphanOrgsLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Building2 className="w-4 h-4 mr-2" />
                    )}
                    Fix Orphan Orgs
                  </Button>
                </div>
                {orphanOrgsResult && (
                  <div className="p-3 rounded-lg border bg-blue-50 dark:bg-blue-950/30 border-blue-200">
                    <div className="flex items-center gap-2 mb-1">
                      <CheckCircle className="w-4 h-4 text-blue-600" />
                      <span className="font-medium text-sm">
                        {orphanOrgsResult.total_orphans === 0
                          ? 'No orphan organisations found'
                          : `${orphanOrgsResult.updated} organisation(s) assigned to "Other" sector`}
                      </span>
                    </div>
                    {orphanOrgsResult.details_sample?.length > 0 && (
                      <div className="mt-2 max-h-32 overflow-y-auto space-y-1">
                        {orphanOrgsResult.details_sample.map((d, i) => (
                          <div key={i} className="text-xs text-muted-foreground" data-testid={`orphan-org-${i}`}>
                            {d.name || d.org_id}
                          </div>
                        ))}
                        {orphanOrgsResult.total_orphans > orphanOrgsResult.details_sample.length && (
                          <div className="text-xs italic text-muted-foreground">
                            … and {orphanOrgsResult.total_orphans - orphanOrgsResult.details_sample.length} more
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Fix Orphan Contacts */}
                <div className="flex items-center justify-between pt-2 border-t">
                  <div>
                    <p className="font-medium">Fix Orphan Contacts</p>
                    <p className="text-sm text-muted-foreground">
                      Reassign contacts with no organisation (or invalid org) to a shared "Unknown Organisation" (auto-created if missing).
                    </p>
                  </div>
                  <Button
                    onClick={fixOrphanContacts}
                    disabled={orphanContactsLoading}
                    variant="outline"
                    data-testid="fix-orphan-contacts-btn"
                  >
                    {orphanContactsLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Users className="w-4 h-4 mr-2" />
                    )}
                    Fix Orphan Contacts
                  </Button>
                </div>
                {orphanContactsResult && (
                  <div className="p-3 rounded-lg border bg-blue-50 dark:bg-blue-950/30 border-blue-200">
                    <div className="flex items-center gap-2 mb-1">
                      <CheckCircle className="w-4 h-4 text-blue-600" />
                      <span className="font-medium text-sm">
                        {orphanContactsResult.total_orphans === 0
                          ? 'No orphan contacts found'
                          : `${orphanContactsResult.updated} contact(s) reassigned to "Unknown Organisation"`}
                      </span>
                    </div>
                    {orphanContactsResult.details_sample?.length > 0 && (
                      <div className="mt-2 max-h-32 overflow-y-auto space-y-1">
                        {orphanContactsResult.details_sample.map((d, i) => (
                          <div key={i} className="text-xs text-muted-foreground" data-testid={`orphan-contact-${i}`}>
                            {d.name || d.email || d.contact_id}
                          </div>
                        ))}
                        {orphanContactsResult.total_orphans > orphanContactsResult.details_sample.length && (
                          <div className="text-xs italic text-muted-foreground">
                            … and {orphanContactsResult.total_orphans - orphanContactsResult.details_sample.length} more
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Cross-Org Note References Scanner */}
                <div className="flex items-center justify-between p-3 rounded-lg border" data-testid="cross-org-scan-card">
                  <div>
                    <p className="font-medium">Scan Notes for Cross-Org References</p>
                    <p className="text-sm text-muted-foreground">
                      Flags notes whose content mentions a DIFFERENT organisation&apos;s name (potential mis-imports).
                    </p>
                  </div>
                  <Button
                    onClick={startCrossOrgNoteScan}
                    disabled={crossOrgScanLoading}
                    variant="outline"
                    data-testid="cross-org-scan-btn"
                  >
                    {crossOrgScanLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <AlertTriangle className="w-4 h-4 mr-2" />
                    )}
                    {crossOrgScanLoading ? 'Scanning…' : 'Scan Notes'}
                  </Button>
                </div>
                {crossOrgScanStatus && (
                  <div className="p-3 rounded-lg border bg-amber-50 dark:bg-amber-950/30 border-amber-200" data-testid="cross-org-scan-results">
                    <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-600" />
                        <span className="font-medium text-sm">
                          {crossOrgScanStatus.status === 'running' && (
                            <>Scanning… {crossOrgScanStatus.scanned} / {crossOrgScanStatus.total} notes</>
                          )}
                          {crossOrgScanStatus.status === 'completed' && (
                            <>{crossOrgScanStatus.match_count} potential cross-org reference(s) across {crossOrgScanStatus.scanned} notes</>
                          )}
                          {crossOrgScanStatus.status === 'failed' && (
                            <>Scan failed: {crossOrgScanStatus.error}</>
                          )}
                        </span>
                      </div>
                      {crossOrgScanStatus.status === 'completed' && crossOrgScanStatus.match_count > 0 && (
                        <div className="flex items-center gap-2">
                          <Input
                            placeholder="Filter by org or text…"
                            value={crossOrgScanFilter}
                            onChange={(e) => setCrossOrgScanFilter(e.target.value)}
                            className="h-8 w-48 text-xs"
                            data-testid="cross-org-scan-filter"
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={exportCrossOrgScanCSV}
                            data-testid="cross-org-scan-export-csv"
                          >
                            <Download className="w-3 h-3 mr-1" />
                            CSV
                          </Button>
                        </div>
                      )}
                    </div>
                    {crossOrgScanStatus.diagnostics && crossOrgScanStatus.status === 'completed' && (
                      <div className="text-[11px] text-muted-foreground mt-1 mb-2 pb-2 border-b border-amber-200/60 grid grid-cols-2 gap-x-4 gap-y-0.5" data-testid="cross-org-scan-diagnostics">
                        <div>From <code>communication_notes</code>: <strong className="tabular-nums">{crossOrgScanStatus.diagnostics.scanned_communication_notes}</strong></div>
                        <div>From <code>organizations[].notes</code>: <strong className="tabular-nums">{crossOrgScanStatus.diagnostics.scanned_embedded_notes}</strong></div>
                        <div>Orgs scanned: <strong className="tabular-nums">{crossOrgScanStatus.diagnostics.orgs_total}</strong></div>
                        <div>Orgs with <code>notes</code> array: <strong className="tabular-nums">{crossOrgScanStatus.diagnostics.orgs_with_notes_array}</strong></div>
                        {crossOrgScanStatus.diagnostics.non_array_notes_samples?.length > 0 && (
                          <div className="col-span-2 text-red-700 dark:text-red-400">
                            <details>
                              <summary className="cursor-pointer">⚠ {crossOrgScanStatus.diagnostics.non_array_notes_samples.length} org(s) have non-array <code>notes</code> field — show samples</summary>
                              <pre className="text-[10px] mt-1 p-1 bg-red-50 dark:bg-red-950/30 rounded overflow-x-auto">
{JSON.stringify(crossOrgScanStatus.diagnostics.non_array_notes_samples, null, 2)}
                              </pre>
                            </details>
                          </div>
                        )}
                      </div>
                    )}
                    {crossOrgScanStatus.matches?.length > 0 && (
                      <div className="max-h-80 overflow-y-auto space-y-2 mt-2">
                        {crossOrgScanStatus.matches
                          .filter((m) => {
                            const f = crossOrgScanFilter.trim().toLowerCase();
                            if (!f) return true;
                            return (
                              (m.current_org_name || '').toLowerCase().includes(f) ||
                              (m.matched_org_name || '').toLowerCase().includes(f) ||
                              (m.matched_text || '').toLowerCase().includes(f) ||
                              (m.snippet || '').toLowerCase().includes(f)
                            );
                          })
                          .slice(0, 200)
                          .map((m, idx) => (
                            <div
                              key={`${m.note_id}_${m.matched_org_id}_${idx}`}
                              className="p-2 rounded border bg-background text-xs"
                              data-testid={`cross-org-match-${idx}`}
                            >
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <Badge variant="secondary" className="text-[10px]">{m.current_org_name || m.current_org_id}</Badge>
                                <span className="text-muted-foreground">&rarr;</span>
                                <Badge variant="destructive" className="text-[10px]">mentions: {m.matched_org_name}</Badge>
                                {m.note_timestamp && (
                                  <span className="text-muted-foreground">{m.note_timestamp}</span>
                                )}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-6 px-2 text-[10px] ml-auto"
                                  onClick={async () => {
                                    if (!window.confirm(`Move this note from "${m.current_org_name}" to "${m.matched_org_name}"?`)) return;
                                    try {
                                      const res = await authJsonFetch(`${API}/admin/database/move-cross-org-note`, {
                                        method: 'POST',
                                        body: JSON.stringify({ note_id: m.note_id, target_org_id: m.matched_org_id }),
                                      });
                                      if (!res.ok) {
                                        const err = await res.json().catch(() => ({}));
                                        toast.error(err.detail || 'Move failed');
                                        return;
                                      }
                                      const data = await res.json();
                                      toast.success(`Moved to ${data.to_org_name}`);
                                      // Drop this match from the local UI list
                                      setCrossOrgScanStatus((prev) => prev ? {
                                        ...prev,
                                        matches: (prev.matches || []).filter((_, i) => i !== idx),
                                        match_count: Math.max(0, (prev.match_count || 0) - 1),
                                      } : prev);
                                    } catch (_e) {
                                      toast.error('Move failed');
                                    }
                                  }}
                                  data-testid={`cross-org-match-move-${idx}`}
                                >
                                  Move to {m.matched_org_name}
                                </Button>
                              </div>
                              <p className="text-muted-foreground whitespace-pre-wrap">{m.snippet}</p>
                            </div>
                          ))}
                        {crossOrgScanStatus.match_count > 200 && (
                          <div className="text-xs italic text-muted-foreground">
                            Showing first 200 of {crossOrgScanStatus.match_count}. Use CSV export for the full list.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Clear All Communication Notes (with optional date range) */}
                <div className="flex items-center justify-between p-3 rounded-lg border border-destructive/40 bg-destructive/5" data-testid="clear-comm-notes-card">
                  <div>
                    <p className="font-medium">Clear Communication Notes</p>
                    <p className="text-sm text-muted-foreground">
                      Delete communication notes — optionally restricted to a date range on the original Maximizer timestamp. Useful when a specific period was wrongly imported and you want to re-import that period from a corrected PDF without restoring the entire database.
                    </p>
                  </div>
                  <Button
                    onClick={openClearCommNotesDialog}
                    disabled={clearNotesLoading}
                    variant="destructive"
                    data-testid="clear-comm-notes-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Clear Comm Notes
                  </Button>
                </div>

                {/* Normalize Date Fields (DD-MMM-YYYY canonical) */}
                <div className="flex items-center justify-between p-3 rounded-lg border border-amber-400/60 bg-amber-50 dark:bg-amber-950/30" data-testid="normalize-dates-card">
                  <div>
                    <p className="font-medium">Normalize Date Fields to DD-MMM-YYYY</p>
                    <p className="text-sm text-muted-foreground">
                      Rewrites every date value across sales, workflows, refunds and organizations into the canonical <code className="px-1 py-0.5 rounded bg-muted">DD-MMM-YYYY</code> format (e.g. <code className="px-1 py-0.5 rounded bg-muted">15-Jun-2026</code>). Converts Excel serials (<code className="px-1 py-0.5 rounded bg-muted">46188</code>), ISO strings, slash dates, and more. Non-date values (e.g. "Yes"/"No") are left untouched. Always run a Dry-Run first.
                    </p>
                  </div>
                  <Button
                    onClick={openNormalizeDatesDialog}
                    disabled={normalizeDatesLoading}
                    variant="outline"
                    className="border-amber-500 text-amber-900 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-950/50"
                    data-testid="normalize-dates-btn"
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    Normalize Dates
                  </Button>
                </div>
                {clearNotesResult && (
                  <div className="p-3 rounded-lg border bg-blue-50 dark:bg-blue-950/30 border-blue-200" data-testid="clear-comm-notes-result">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-blue-600" />
                      <span className="font-medium text-sm">{clearNotesResult.message}</span>
                    </div>
                    {clearNotesResult.total_before > 0 && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {clearNotesResult.deleted} of {clearNotesResult.total_before} notes deleted.
                      </p>
                    )}
                  </div>
                )}

                {/* Clear Comm Notes Dialog */}
                <Dialog open={clearNotesDialogOpen} onOpenChange={setClearNotesDialogOpen}>
                  <DialogContent className="max-w-lg" data-testid="clear-comm-notes-dialog">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2 text-destructive">
                        <Trash2 className="w-5 h-5" /> Clear Communication Notes
                      </DialogTitle>
                      <DialogDescription>
                        Permanently delete communication notes. Leave both dates empty to delete ALL notes; otherwise the range filter is applied (inclusive both ends) on each note&apos;s original Maximizer timestamp.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs text-muted-foreground block mb-1">From (inclusive)</label>
                          <Input
                            type="date"
                            value={clearNotesFromDate}
                            onChange={(e) => { setClearNotesFromDate(e.target.value); setClearNotesPreview(null); }}
                            data-testid="clear-comm-notes-from"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-muted-foreground block mb-1">To (inclusive)</label>
                          <Input
                            type="date"
                            value={clearNotesToDate}
                            onChange={(e) => { setClearNotesToDate(e.target.value); setClearNotesPreview(null); }}
                            data-testid="clear-comm-notes-to"
                          />
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={previewClearCommNotes}
                        disabled={clearNotesPreviewLoading}
                        data-testid="clear-comm-notes-preview-btn"
                      >
                        {clearNotesPreviewLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
                        Preview count
                      </Button>
                      {clearNotesPreview && (
                        <div className="p-3 rounded border bg-amber-50 dark:bg-amber-950/30 border-amber-200" data-testid="clear-comm-notes-preview">
                          <div className="flex items-center gap-2 mb-1">
                            <AlertTriangle className="w-4 h-4 text-amber-600" />
                            <span className="font-medium text-sm">
                              {clearNotesPreview.total} note(s) will be deleted
                              {clearNotesPreview.from_date || clearNotesPreview.to_date
                                ? ` between ${clearNotesPreview.from_date || 'beginning'} and ${clearNotesPreview.to_date || 'now'}`
                                : ' (ALL notes — no date filter)'}
                            </span>
                          </div>
                          {clearNotesPreview.sample?.length > 0 && (
                            <div className="text-xs text-muted-foreground space-y-1 mt-2 max-h-32 overflow-y-auto">
                              <div className="font-medium">Sample:</div>
                              {clearNotesPreview.sample.map((s, i) => (
                                <div key={i}>• {s.original_timestamp || s.date_iso} — {s.creator || '—'}</div>
                              ))}
                            </div>
                          )}
                          {clearNotesPreview.diagnostics && (clearNotesPreview.from_date || clearNotesPreview.to_date) && (
                            <div className="text-xs text-muted-foreground mt-3 pt-2 border-t border-amber-200/60 space-y-0.5" data-testid="clear-comm-notes-diagnostics">
                              <div className="font-medium text-amber-900 dark:text-amber-200">Diagnostics</div>
                              <div>Total notes scanned (both stores): <strong className="tabular-nums">{clearNotesPreview.diagnostics.total_scanned}</strong></div>
                              <div>Parsed (recognised date): <strong className="tabular-nums">{clearNotesPreview.diagnostics.parsed}</strong></div>
                              <div className="pl-3 text-[11px]">
                                · From <code>communication_notes</code> (in-app): <strong className="tabular-nums">{clearNotesPreview.diagnostics.from_communication_notes ?? 0}</strong>
                              </div>
                              <div className="pl-3 text-[11px]">
                                · From <code>organizations[].notes</code> (PDF-imported): <strong className="tabular-nums">{clearNotesPreview.diagnostics.from_imported_notes ?? 0}</strong>
                              </div>
                              <div>Earlier than {clearNotesPreview.from_date || 'from-date'} (excluded): <strong className="tabular-nums">{clearNotesPreview.diagnostics.earlier_than_from}</strong></div>
                              <div>Later than {clearNotesPreview.to_date || 'to-date'} (excluded): <strong className="tabular-nums">{clearNotesPreview.diagnostics.later_than_to}</strong></div>
                              {clearNotesPreview.diagnostics.unparseable > 0 && (
                                <div className="text-red-700 dark:text-red-400">
                                  Unparseable timestamps: <strong className="tabular-nums">{clearNotesPreview.diagnostics.unparseable}</strong>
                                  {clearNotesPreview.diagnostics.unparseable_samples?.length > 0 && (
                                    <details className="mt-1">
                                      <summary className="cursor-pointer">Show {clearNotesPreview.diagnostics.unparseable_samples.length} sample(s)</summary>
                                      <pre className="text-[10px] mt-1 p-1 bg-red-50 dark:bg-red-950/30 rounded overflow-x-auto">
{JSON.stringify(clearNotesPreview.diagnostics.unparseable_samples, null, 2)}
                                      </pre>
                                    </details>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                      <div className="border-t pt-3">
                        <label className="text-xs text-muted-foreground block mb-1">
                          Type <strong>DELETE</strong> to confirm
                        </label>
                        <Input
                          value={clearNotesConfirmText}
                          onChange={(e) => setClearNotesConfirmText(e.target.value)}
                          placeholder="DELETE"
                          data-testid="clear-comm-notes-confirm"
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setClearNotesDialogOpen(false)}>Cancel</Button>
                      <Button
                        variant="destructive"
                        onClick={clearCommNotes}
                        disabled={clearNotesLoading || clearNotesConfirmText !== 'DELETE'}
                        data-testid="clear-comm-notes-confirm-btn"
                      >
                        {clearNotesLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
                        Delete {clearNotesPreview?.total ? `${clearNotesPreview.total} note(s)` : 'notes'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>

                {/* Normalize Dates Dialog */}
                <Dialog open={normalizeDatesOpen} onOpenChange={setNormalizeDatesOpen}>
                  <DialogContent className="max-w-3xl" data-testid="normalize-dates-dialog">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <Calendar className="w-5 h-5" /> Normalize Date Fields → DD-MMM-YYYY
                      </DialogTitle>
                      <DialogDescription>
                        Rewrites every date value across <strong>sales_data</strong>, <strong>workflows</strong>, <strong>refunds</strong>, and <strong>organizations</strong> into the canonical <code className="px-1 rounded bg-muted">DD-MMM-YYYY</code> format. Handles Excel serials, ISO, slash and dash formats automatically. Non-date values (like "Yes"/"No") are left untouched.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
                      <div className="flex items-center gap-2">
                        <Button
                          onClick={runNormalizeDatesDry}
                          disabled={normalizeDatesLoading}
                          variant="outline"
                          data-testid="normalize-dates-dry-btn"
                        >
                          {normalizeDatesLoading && !normalizeDatesDry ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
                          Dry-Run (preview changes)
                        </Button>
                        {normalizeDatesDry && (
                          <Badge variant="secondary" data-testid="normalize-dates-dry-summary">
                            {normalizeDatesDry.total_changed} value(s) would change · {normalizeDatesDry.total_unchanged} already correct
                          </Badge>
                        )}
                      </div>

                      {normalizeDatesDry && (
                        <div className="space-y-3" data-testid="normalize-dates-dry-result">
                          {Object.entries(normalizeDatesDry.by_collection || {}).map(([coll, fieldStats]) => {
                            const entries = Object.entries(fieldStats || {}).filter(([, s]) => (s?.scanned || 0) > 0);
                            if (entries.length === 0) return null;
                            return (
                              <div key={coll} className="border rounded-lg p-3">
                                <div className="font-medium mb-2 text-sm">{coll}</div>
                                <table className="w-full text-xs">
                                  <thead className="text-muted-foreground">
                                    <tr>
                                      <th className="text-left font-normal pb-1">Field</th>
                                      <th className="text-right font-normal pb-1">Scanned</th>
                                      <th className="text-right font-normal pb-1">Would change</th>
                                      <th className="text-left font-normal pb-1 pl-4">Sample (before → after)</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {entries.map(([field, s]) => (
                                      <tr key={field} className="border-t">
                                        <td className="py-1 pr-2 font-mono">{field}</td>
                                        <td className="py-1 text-right tabular-nums">{s.scanned}</td>
                                        <td className={cn(
                                          "py-1 text-right tabular-nums",
                                          s.would_change > 0 ? "text-amber-700 dark:text-amber-400 font-medium" : ""
                                        )}>{s.would_change}</td>
                                        <td className="py-1 pl-4 text-muted-foreground">
                                          {(s.samples || []).slice(0, 3).map((sp, idx) => (
                                            <div key={idx} className="truncate">
                                              <code>{String(sp.before)}</code> → <code>{sp.after}</code>
                                            </div>
                                          ))}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {normalizeDatesDry && normalizeDatesDry.total_changed > 0 && !normalizeDatesExecuted && (
                        <div className="border-t pt-3 space-y-2">
                          <Label htmlFor="normalize-confirm" className="text-sm">
                            Type <strong>NORMALIZE</strong> to apply these changes to the database
                          </Label>
                          <Input
                            id="normalize-confirm"
                            data-testid="normalize-dates-confirm-input"
                            value={normalizeDatesConfirm}
                            onChange={(e) => setNormalizeDatesConfirm(e.target.value)}
                            placeholder="NORMALIZE"
                          />
                        </div>
                      )}

                      {normalizeDatesExecuted && (
                        <div className="p-3 rounded-lg border bg-green-50 dark:bg-green-950/30 border-green-200" data-testid="normalize-dates-result">
                          <div className="flex items-center gap-2 mb-1">
                            <CheckCircle className="w-4 h-4 text-green-600" />
                            <span className="font-medium text-sm">Normalization complete</span>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {normalizeDatesExecuted.total_changed} value(s) normalized · {normalizeDatesExecuted.total_unchanged} already correct · {normalizeDatesExecuted.total_scanned} total scanned
                          </p>
                        </div>
                      )}
                    </div>
                    <DialogFooter>
                      <Button
                        variant="ghost"
                        onClick={() => setNormalizeDatesOpen(false)}
                        data-testid="normalize-dates-cancel-btn"
                      >
                        Close
                      </Button>
                      <Button
                        onClick={executeNormalizeDates}
                        disabled={
                          normalizeDatesLoading ||
                          !normalizeDatesDry ||
                          normalizeDatesDry.total_changed === 0 ||
                          normalizeDatesConfirm !== 'NORMALIZE' ||
                          !!normalizeDatesExecuted
                        }
                        data-testid="normalize-dates-execute-btn"
                      >
                        {normalizeDatesLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Calendar className="w-4 h-4 mr-1" />}
                        Apply {normalizeDatesDry?.total_changed || 0} change(s)
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardContent>
              )}
            </Card>

            {/* Database Tools Card (collapsible — default collapsed) */}
            <Card className={cn(isBarbiePink && "card-brutalist")}>
              <CardHeader
                className="cursor-pointer select-none hover:bg-muted/40 transition-colors"
                onClick={() => setDbToolsExpanded((v) => !v)}
                data-testid="dbtools-toggle"
              >
                <CardTitle className="flex items-center gap-2">
                  <Database className="w-5 h-5 text-primary" />
                  Database Tools
                  <span className="ml-auto text-xs font-normal text-muted-foreground">
                    {dbToolsExpanded ? 'Click to collapse' : 'Click to expand'}
                  </span>
                  {dbToolsExpanded ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </CardTitle>
                <CardDescription>Advanced database operations</CardDescription>
              </CardHeader>
              {dbToolsExpanded && (
              <CardContent className="space-y-4">
                {/* XLS Export/Import Section */}
                <div className="p-4 border rounded-lg bg-primary/5">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-semibold flex items-center gap-2">
                        <FileSpreadsheet className="w-4 h-4 text-primary" />
                        XLS Import/Export
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Export database to Excel or import data from Excel files
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={exportDatabaseToXLS} disabled={xlsExportLoading}>
                      {xlsExportLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4 mr-2" />
                      )}
                      Export to XLS
                    </Button>
                    <Button variant="outline" onClick={() => setXlsImportDialogOpen(true)}>
                      <Upload className="w-4 h-4 mr-2" />
                      Import from XLS
                    </Button>
                  </div>
                </div>

                <div className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold flex items-center gap-2">
                        <FlaskConical className="w-4 h-4" />
                        Stress Test
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Add 50 random test records to each table
                      </p>
                    </div>
                    <Button onClick={() => setStressTestDialogOpen(true)}>
                      Run Stress Test
                    </Button>
                  </div>
                </div>

                <div className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold flex items-center gap-2">
                        <Trash2 className="w-4 h-4" />
                        Clear Test Data
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Remove all test data (prefixed records only)
                      </p>
                    </div>
                    <Button variant="outline" onClick={clearStressTestData} disabled={stressTestLoading}>
                      {stressTestLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Clear Test Data
                    </Button>
                  </div>
                </div>

                <div className="p-4 border border-destructive/50 rounded-lg bg-destructive/5">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold flex items-center gap-2 text-destructive">
                        <AlertTriangle className="w-4 h-4" />
                        Clear ALL Data
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Remove all operational data. Templates can be preserved.
                      </p>
                    </div>
                    <Button variant="destructive" onClick={() => setClearAllDataDialog(true)}>
                      Clear All Data
                    </Button>
                  </div>
                </div>

                <div className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold flex items-center gap-2">
                        <Upload className="w-4 h-4" />
                        Restore Database
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Upload a backup file to restore data
                      </p>
                    </div>
                    <Button variant="outline" onClick={() => setRestoreDialogOpen(true)}>
                      Restore from Backup
                    </Button>
                  </div>
                </div>

                <div className="p-4 border border-amber-500/50 rounded-lg bg-amber-500/5">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold flex items-center gap-2 text-amber-700 dark:text-amber-400">
                        <Clock className="w-4 h-4" />
                        Point-in-Time Recovery
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Restore the database to an exact moment using the latest backup before that time + transaction-log replay.
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => setPitrDialogOpen(true)}
                      data-testid="open-pitr-dialog-btn"
                    >
                      Run PITR
                    </Button>
                  </div>
                </div>

                <div className="p-4 border border-blue-500/50 rounded-lg bg-blue-500/5">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold flex items-center gap-2 text-blue-700 dark:text-blue-400">
                        <FileSpreadsheet className="w-4 h-4" />
                        Legacy Maximizer Reconcile
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Upload a Maximizer extract (Accounts / Photography / Production / Sales) to spot field-by-field differences. Nothing is written until you tick the rows you want — and a backup is created automatically before each commit.
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => setReconcileDialogOpen(true)}
                      data-testid="open-reconcile-dialog-btn"
                    >
                      Open Reconcile
                    </Button>
                  </div>
                </div>
              </CardContent>
              )}
            </Card>

            {/* Database Backups Card */}
            <Card className={cn(isBarbiePink && "card-brutalist")}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Folder className="w-5 h-5 text-primary" />
                  Database Backups
                </CardTitle>
                <CardDescription>
                  Create and download database backups for restoration. Automatic backups run daily at 1:00 PM and 7:00 PM Sydney time.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Size limits info */}
                <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-blue-800 dark:text-blue-200">
                      <p className="font-medium mb-1">Database Size Limits</p>
                      <ul className="text-xs space-y-1 text-blue-700 dark:text-blue-300">
                        <li>• Maximum backup size: <strong>5 GB</strong></li>
                        <li>• Maximum restore file size: <strong>5 GB</strong></li>
                        <li>• Large backups may take several minutes to create/download</li>
                        <li>• Backups are uploaded to GS-Workflow Shared Drive</li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 mb-4 flex-wrap">
                  <Button onClick={createBackup} disabled={reportLoading} data-testid="create-backup-btn">
                    {reportLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Plus className="w-4 h-4 mr-2" />
                    )}
                    Create Backup Now
                  </Button>
                  <Button 
                    variant="secondary" 
                    onClick={downloadDatabase} 
                    disabled={downloadingDatabase}
                    data-testid="download-database-btn"
                  >
                    {downloadingDatabase ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    Download Database
                  </Button>
                  <Button variant="outline" onClick={fetchBackups}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh
                  </Button>
                </div>
                
                {/* Scheduled Backup Info */}
                <div className="p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg mb-4">
                  <p className="text-sm text-blue-800 dark:text-blue-200 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    <span><strong>Scheduled Backups:</strong> Daily at 1:00 PM &amp; 7:00 PM Sydney time (uploaded to Google Drive BackupDB folder)</span>
                  </p>
                </div>
                
                {/* Drive Connection Status */}
                {!driveConnected && backups.length > 0 && (
                  <div className="p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg mb-4">
                    <p className="text-sm text-amber-800 dark:text-amber-200 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      <span><strong>Note:</strong> Could not connect to Google Drive. Only showing local backups. Connect Gmail/Drive to see cloud backups.</span>
                    </p>
                  </div>
                )}

                {/* Backend warnings + counts */}
                {(backupWarnings.length > 0 || (backupCounts.local || backupCounts.drive)) && (
                  <div className="mb-4 space-y-2" data-testid="backup-warnings-block">
                    {(backupCounts.local > 0 || backupCounts.drive > 0) && (
                      <p className="text-xs text-muted-foreground">
                        <strong>{backupCounts.drive}</strong> on Google Drive &middot; <strong>{backupCounts.local}</strong> local (this pod). Local backups older than {backupCounts.retention_days} days are auto-deleted; the pod disk is also wiped on every deploy — Drive copies are the source of truth.
                      </p>
                    )}
                    {backupWarnings.map((w, i) => (
                      <div
                        key={i}
                        className="p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg"
                        data-testid={`backup-warning-${i}`}
                      >
                        <p className="text-sm text-amber-900 dark:text-amber-100 flex items-start gap-2">
                          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                          <span>{w}</span>
                        </p>
                      </div>
                    ))}
                  </div>
                )}

                {backups.length > 0 ? (
                  <div>
                    <div className={cn(backupListExpanded ? "max-h-[400px] overflow-y-auto" : "")}>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Filename</TableHead>
                            <TableHead>Size</TableHead>
                            <TableHead>Storage</TableHead>
                            <TableHead>Created</TableHead>
                            <TableHead></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(backupListExpanded ? backups : backups.slice(0, 3)).map((backup) => (
                        <TableRow key={backup.filename}>
                          <TableCell className="font-mono text-sm">{backup.filename}</TableCell>
                          <TableCell>{backup.size_mb} MB</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1.5">
                              {(() => {
                                const locs = backup.storage_locations || [];
                                const hasLocal = locs.includes('local');
                                const hasDrive = locs.includes('drive');
                                if (hasDrive && hasLocal) {
                                  return (
                                    <span title={`Stored on Google Drive (${backup.drive_folder || 'BackupDB'}) and local pod disk`} className="flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400" data-testid="backup-storage-both">
                                      <Cloud className="w-4 h-4" />
                                      <HardDrive className="w-3.5 h-3.5 text-muted-foreground" />
                                      <span className="font-medium">Drive + Local</span>
                                    </span>
                                  );
                                }
                                if (hasDrive) {
                                  return (
                                    <span title={`Stored on Google Drive (${backup.drive_folder || 'BackupDB'}) — safe across deploys`} className="flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400" data-testid="backup-storage-drive">
                                      <Cloud className="w-4 h-4" />
                                      <span className="font-medium">Drive</span>
                                    </span>
                                  );
                                }
                                if (hasLocal) {
                                  return (
                                    <span title="LOCAL ONLY — pod disk is ephemeral. This backup will be lost on the next deploy. Re-upload it to Drive ASAP." className="flex items-center gap-1 text-xs text-amber-700 dark:text-amber-400" data-testid="backup-storage-local-only">
                                      <HardDrive className="w-4 h-4" />
                                      <span className="font-medium">Local only</span>
                                      <AlertTriangle className="w-3.5 h-3.5" />
                                    </span>
                                  );
                                }
                                return (
                                  <span className="text-xs text-muted-foreground">
                                    <HardDrive className="w-4 h-4" />
                                  </span>
                                );
                              })()}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">
                            {new Date(backup.created_at).toLocaleString('en-AU', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              timeZone: 'Australia/Sydney'
                            })}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1 justify-end">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => downloadBackup(backup.filename)}
                                title="Download"
                              >
                                <FileDown className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-destructive hover:bg-destructive hover:text-white"
                                onClick={() => setBackupToDelete(backup.filename)}
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                        </TableBody>
                      </Table>
                    </div>
                    {backups.length > 3 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full mt-2 text-muted-foreground"
                        onClick={() => setBackupListExpanded(!backupListExpanded)}
                        data-testid="toggle-backup-list"
                      >
                        {backupListExpanded 
                          ? `Show less (${backups.length} backups)` 
                          : `Show all ${backups.length} backups (${backups.length - 3} more)`
                        }
                      </Button>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No backups available yet.</p>
                )}
                
                {/* Error Logs Section */}
                <div className="mt-4 pt-4 border-t">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-sm">Backup Debug Logs</h4>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        setShowBackupLogs(!showBackupLogs);
                        if (!showBackupLogs) fetchBackupErrorLogs();
                      }}
                    >
                      {showBackupLogs ? 'Hide Logs' : 'Show Debug Logs'}
                    </Button>
                  </div>
                  
                  {showBackupLogs && (
                    <div className="space-y-2">
                      {loadingBackupLogs ? (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Loading logs...
                        </div>
                      ) : backupErrorLogs.length > 0 ? (
                        <div className="max-h-96 overflow-y-auto space-y-2">
                          {backupErrorLogs.map((log, idx) => (
                            <div 
                              key={idx} 
                              className={`p-3 rounded-lg text-xs font-mono ${
                                log.success 
                                  ? 'bg-green-50 dark:bg-green-950/20 border border-green-200' 
                                  : 'bg-red-50 dark:bg-red-950/20 border border-red-200'
                              }`}
                            >
                              <div className="flex justify-between items-start mb-2">
                                <span className={log.success ? 'text-green-700' : 'text-red-700'}>
                                  {log.success ? '✅ Success' : '❌ Failed'}
                                </span>
                                <span className="text-muted-foreground">
                                  {new Date(log.timestamp).toLocaleString('en-AU', { timeZone: 'Australia/Sydney' })}
                                </span>
                              </div>
                              <div className="text-muted-foreground mb-1">User: {log.user_email}</div>
                              {log.error && (
                                <div className="text-red-600 dark:text-red-400 mb-2">
                                  <strong>Error:</strong> {log.error}
                                </div>
                              )}
                              <details className="cursor-pointer">
                                <summary className="text-blue-600 hover:underline">View steps ({log.steps?.length || 0})</summary>
                                <pre className="mt-2 p-2 bg-gray-100 dark:bg-gray-900 rounded overflow-x-auto text-xs">
                                  {JSON.stringify(log.steps, null, 2)}
                                </pre>
                              </details>
                              {log.traceback && (
                                <details className="cursor-pointer mt-2">
                                  <summary className="text-red-600 hover:underline">View traceback</summary>
                                  <pre className="mt-2 p-2 bg-gray-100 dark:bg-gray-900 rounded overflow-x-auto text-xs whitespace-pre-wrap">
                                    {log.traceback}
                                  </pre>
                                </details>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">No backup logs recorded yet.</p>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Transaction Logs Card */}
            <TransactionLogsCard isBarbiePink={isBarbiePink} />

            {/* Admin Secrets Card */}
            <Card className={cn(isBarbiePink && "border-pink-200")}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Key className={cn("w-5 h-5", isBarbiePink && "text-pink-500")} />
                  Secrets & Credentials
                </CardTitle>
                <CardDescription>
                  View API keys and integration credentials used by this application
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {!showSecrets ? (
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setShowSecrets(true);
                      fetchSecrets();
                    }}
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Show Secrets
                  </Button>
                ) : (
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <h4 className="font-medium">Application Credentials</h4>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={fetchSecrets} disabled={loadingSecrets}>
                          {loadingSecrets ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setShowSecrets(false)}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    {loadingSecrets ? (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Loading secrets...
                      </div>
                    ) : secrets ? (
                      <div className="space-y-4">
                        {/* API Keys */}
                        <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-900 border">
                          <h5 className="font-medium mb-2 flex items-center gap-2">
                            <Key className="w-4 h-4" />
                            API Keys
                          </h5>
                          <div className="space-y-2">
                            {secrets.api_keys?.map((apiKey, idx) => (
                              <div key={idx} className="flex items-center justify-between text-sm p-2 bg-white dark:bg-gray-800 rounded">
                                <div>
                                  <span className="font-medium">{apiKey.name}</span>
                                  <p className="text-xs text-muted-foreground">{apiKey.description}</p>
                                </div>
                                <div className="flex items-center gap-2">
                                  {apiKey.key ? (
                                    <code className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded font-mono">
                                      {apiKey.key}
                                    </code>
                                  ) : (
                                    <Badge variant="outline" className="text-amber-600">Not Set</Badge>
                                  )}
                                  <Badge variant={apiKey.status === 'configured' ? 'default' : 'secondary'}>
                                    {apiKey.status}
                                  </Badge>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Google Services */}
                        <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-900 border">
                          <h5 className="font-medium mb-2 flex items-center gap-2">
                            <Cloud className="w-4 h-4" />
                            Google Services
                          </h5>
                          <div className="space-y-2 text-sm">
                            {/* OAuth */}
                            <div className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded">
                              <span>OAuth Client ID</span>
                              <div className="flex items-center gap-2">
                                {secrets.google_services?.oauth?.client_id ? (
                                  <code className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded font-mono">
                                    {secrets.google_services.oauth.client_id}
                                  </code>
                                ) : (
                                  <Badge variant="outline" className="text-amber-600">Not Set</Badge>
                                )}
                                <Badge variant={secrets.google_services?.oauth?.status === 'configured' ? 'default' : 'secondary'}>
                                  {secrets.google_services?.oauth?.status}
                                </Badge>
                              </div>
                            </div>
                            
                            {/* Shared Drive */}
                            <div className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded">
                              <div>
                                <span>Shared Drive</span>
                                <p className="text-xs text-muted-foreground">{secrets.google_services?.shared_drive?.name}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                {secrets.google_services?.shared_drive?.drive_id && (
                                  <code className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded font-mono">
                                    {secrets.google_services.shared_drive.drive_id}
                                  </code>
                                )}
                                <Badge variant={secrets.google_services?.shared_drive?.status === 'connected' ? 'default' : 'secondary'}>
                                  {secrets.google_services?.shared_drive?.status}
                                </Badge>
                              </div>
                            </div>
                            
                            {/* Gmail Tokens */}
                            <div className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded">
                              <span>Gmail Connected Users</span>
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{secrets.google_services?.gmail_tokens?.connected_users || 0}</span>
                                <Badge variant={secrets.google_services?.gmail_tokens?.status === 'active' ? 'default' : 'secondary'}>
                                  {secrets.google_services?.gmail_tokens?.status}
                                </Badge>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Database */}
                        <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-900 border">
                          <h5 className="font-medium mb-2 flex items-center gap-2">
                            <Database className="w-4 h-4" />
                            Database
                          </h5>
                          <div className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded text-sm">
                            <div>
                              <span className="font-medium">{secrets.database?.database_name}</span>
                              <p className="text-xs text-muted-foreground">Host: {secrets.database?.host}</p>
                            </div>
                            <Badge variant={secrets.database?.status === 'connected' ? 'default' : 'secondary'}>
                              {secrets.database?.status}
                            </Badge>
                          </div>
                        </div>

                        {/* Integrations */}
                        <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-900 border">
                          <h5 className="font-medium mb-2 flex items-center gap-2">
                            <Link className="w-4 h-4" />
                            Integrations
                          </h5>
                          <div className="space-y-2">
                            {secrets.integrations?.map((integration, idx) => (
                              <div key={idx} className="flex items-center justify-between text-sm p-2 bg-white dark:bg-gray-800 rounded">
                                <span className="font-medium">{integration.name}</span>
                                <div className="flex items-center gap-2">
                                  {integration.consumer_key && (
                                    <code className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded font-mono">
                                      {integration.consumer_key}
                                    </code>
                                  )}
                                  {integration.value && (
                                    <code className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded font-mono max-w-xs truncate">
                                      {integration.value}
                                    </code>
                                  )}
                                  <Badge variant={integration.status === 'configured' ? 'default' : 'secondary'}>
                                    {integration.status}
                                  </Badge>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">No secrets data available</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Disaster Recovery Card */}
            <Card className={cn(isBarbiePink && "border-pink-200")}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Server className={cn("w-5 h-5", isBarbiePink && "text-pink-500")} />
                  Disaster Recovery
                </CardTitle>
                <CardDescription>
                  Database migration tools for backup server setup and emergency recovery
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Current Connection Info */}
                <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-900 border">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium flex items-center gap-2">
                      <Database className="w-4 h-4" />
                      Current Database Connection
                    </h4>
                    <Button variant="ghost" size="sm" onClick={fetchDbConnectionInfo} disabled={loadingDbInfo}>
                      {loadingDbInfo ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    </Button>
                  </div>
                  
                  {dbConnectionInfo ? (
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground">Status:</span>
                        <Badge variant="default" className="bg-green-500">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          {dbConnectionInfo.connection_status}
                        </Badge>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Database:</span> {dbConnectionInfo.database_name}
                      </div>
                      <div>
                        <span className="text-muted-foreground">Host:</span> {dbConnectionInfo.host}
                      </div>
                      <div>
                        <span className="text-muted-foreground">Port:</span> {dbConnectionInfo.port}
                      </div>
                      <div>
                        <span className="text-muted-foreground">Size:</span> {dbConnectionInfo.size_mb} MB
                      </div>
                      <div>
                        <span className="text-muted-foreground">Collections:</span> {dbConnectionInfo.collections}
                      </div>
                    </div>
                  ) : (
                    <Button variant="outline" size="sm" onClick={fetchDbConnectionInfo}>
                      Load Connection Info
                    </Button>
                  )}
                </div>

                {/* Migration Actions */}
                <div className="flex flex-wrap gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      resetMigrationDialog();
                      setShowMigrationDialog(true);
                      fetchMigrationLogs();
                    }}
                  >
                    <Server className="w-4 h-4 mr-2" />
                    Migrate to New Server
                  </Button>
                </div>

                {/* Migration History */}
                {migrationLogs.length > 0 && (
                  <div className="mt-4">
                    <h4 className="font-medium mb-2 text-sm">Migration History</h4>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {migrationLogs.map((log, idx) => (
                        <div key={idx} className="text-xs p-2 rounded bg-gray-100 dark:bg-gray-800">
                          <div className="flex justify-between">
                            <span className="font-medium">{log.migration_id}</span>
                            <span className="text-muted-foreground">
                              {new Date(log.migrated_at).toLocaleString('en-AU', { timeZone: 'Australia/Sydney' })}
                            </span>
                          </div>
                          <div className="text-muted-foreground">
                            {log.source_db} → {log.target_db} • {log.total_documents} docs • by {log.migrated_by}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Migration Dialog */}
            <Dialog open={showMigrationDialog} onOpenChange={setShowMigrationDialog}>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Server className="w-5 h-5" />
                    Migrate Database to New Server
                  </DialogTitle>
                  <DialogDescription>
                    Connect to a new MongoDB instance and migrate all data for disaster recovery or server migration.
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                  {/* Connection Form */}
                  <div className="space-y-3">
                    <div>
                      <Label htmlFor="targetMongoUrl">Target MongoDB URL</Label>
                      <Input
                        id="targetMongoUrl"
                        type="password"
                        placeholder="mongodb+srv://user:password@cluster.mongodb.net/"
                        value={targetMongoUrl}
                        onChange={(e) => setTargetMongoUrl(e.target.value)}
                        className="font-mono text-sm"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Full connection string including credentials
                      </p>
                    </div>
                    
                    <div>
                      <Label htmlFor="targetDbName">Target Database Name</Label>
                      <Input
                        id="targetDbName"
                        placeholder="gs_workflow_backup"
                        value={targetDbName}
                        onChange={(e) => setTargetDbName(e.target.value)}
                      />
                    </div>

                    <div className="flex items-center space-x-2">
                      <Switch
                        id="includeUsers"
                        checked={includeUsersInMigration}
                        onCheckedChange={setIncludeUsersInMigration}
                      />
                      <Label htmlFor="includeUsers" className="text-sm">
                        Include user accounts (will need re-authentication on new server)
                      </Label>
                    </div>
                  </div>

                  {/* Test Connection Button */}
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      onClick={testNewDbConnection}
                      disabled={testingConnection || !targetMongoUrl || !targetDbName}
                    >
                      {testingConnection ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Testing...
                        </>
                      ) : (
                        <>
                          <Link className="w-4 h-4 mr-2" />
                          Test Connection
                        </>
                      )}
                    </Button>
                  </div>

                  {/* Connection Test Result */}
                  {connectionTestResult && (
                    <div className={cn(
                      "p-3 rounded-lg border",
                      connectionTestResult.success 
                        ? "bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800"
                        : "bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800"
                    )}>
                      {connectionTestResult.success ? (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                            <CheckCircle className="w-4 h-4" />
                            <span className="font-medium">Connection Successful!</span>
                          </div>
                          <div className="text-sm grid grid-cols-2 gap-1">
                            <span>Database: {connectionTestResult.database_name}</span>
                            <span>Size: {connectionTestResult.size_mb} MB</span>
                            <span>Collections: {connectionTestResult.collections_count}</span>
                            <span>Documents: {connectionTestResult.objects}</span>
                          </div>
                          {connectionTestResult.collections_count > 0 && (
                            <p className="text-xs text-amber-600 dark:text-amber-400">
                              ⚠️ Target database is not empty. Migration will overwrite existing data.
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
                          <XCircle className="w-4 h-4" />
                          <span>{connectionTestResult.error}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Migration Result */}
                  {migrationResult && (
                    <div className={cn(
                      "p-3 rounded-lg border",
                      migrationResult.success 
                        ? "bg-green-50 border-green-200 dark:bg-green-900/20"
                        : "bg-red-50 border-red-200 dark:bg-red-900/20"
                    )}>
                      {migrationResult.success ? (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                            <CheckCircle className="w-4 h-4" />
                            <span className="font-medium">{migrationResult.message}</span>
                          </div>
                          <details className="text-sm">
                            <summary className="cursor-pointer text-blue-600">View migrated collections</summary>
                            <pre className="mt-2 p-2 bg-white dark:bg-gray-800 rounded text-xs overflow-auto max-h-40">
                              {JSON.stringify(migrationResult.collections_migrated, null, 2)}
                            </pre>
                          </details>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
                          <XCircle className="w-4 h-4" />
                          <span>{migrationResult.error}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowMigrationDialog(false)}>
                    Close
                  </Button>
                  <Button 
                    onClick={executeMigration}
                    disabled={!connectionTestResult?.success || migrating}
                    variant="default"
                  >
                    {migrating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Migrating...
                      </>
                    ) : (
                      <>
                        <ArrowRight className="w-4 h-4 mr-2" />
                        Start Migration
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Stress Test Confirmation */}
            <AlertDialog open={stressTestDialogOpen} onOpenChange={setStressTestDialogOpen}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-yellow-500" />
                    Run Stress Test?
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    This will create 50 random records in each of these tables:
                    <ul className="list-disc list-inside mt-2">
                      <li>Organizations</li>
                      <li>Contacts</li>
                      <li>Workflows</li>
                      <li>Email Templates</li>
                      <li>Sent Emails</li>
                    </ul>
                    <p className="mt-2">Total: 250 test records</p>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={runStressTest} disabled={stressTestLoading}>
                    {stressTestLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Run Test
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            {/* Clear All Data Confirmation */}
            <AlertDialog open={clearAllDataDialog} onOpenChange={setClearAllDataDialog}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle className="flex items-center gap-2 text-destructive">
                    <AlertTriangle className="w-5 h-5" />
                    Clear ALL Data?
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently delete ALL operational data including organizations, 
                    contacts, workflows, sent emails, and more.
                    <div className="mt-4 p-3 bg-muted rounded-lg space-y-3">
                      <p className="text-sm font-medium text-foreground">Preserve the following:</p>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={preserveEmailTemplates}
                          onChange={(e) => setPreserveEmailTemplates(e.target.checked)}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">Email Templates</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={preserveWorkflowTemplates}
                          onChange={(e) => setPreserveWorkflowTemplates(e.target.checked)}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">Workflow Templates</span>
                      </label>
                    </div>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={clearAllData} 
                    className="bg-destructive hover:bg-destructive/90"
                    disabled={clearDataLoading}
                  >
                    {clearDataLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Clear All Data
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            {/* Restore Database Dialog */}
            <Dialog open={restoreDialogOpen} onOpenChange={setRestoreDialogOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Upload className="w-5 h-5" />
                    Restore Database
                  </DialogTitle>
                  <DialogDescription>
                    Upload a backup file to restore the database. This will replace existing data.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <Label>Backup File (.zip)</Label>
                    <Input
                      type="file"
                      accept=".zip"
                      onChange={(e) => setRestoreFile(e.target.files[0])}
                      className="mt-1"
                    />
                    {restoreFile && (
                      <p className="text-sm text-muted-foreground mt-1">
                        Selected: {restoreFile.name}
                      </p>
                    )}
                  </div>
                  <div className="p-3 bg-muted rounded-lg space-y-3">
                    <p className="text-sm font-medium text-foreground">Restore options:</p>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={includeSystemCollections}
                        onChange={(e) => setIncludeSystemCollections(e.target.checked)}
                        className="w-4 h-4 mt-0.5"
                        data-testid="restore-include-system-collections"
                      />
                      <span className="text-sm">
                        <span className="font-medium">Restore system collections</span>
                        <span className="block text-xs text-muted-foreground">
                          Includes users, sessions, gmail tokens, oauth states, and system_config. Recommended for a 100% restore. Untick if restoring data into a different environment where you want to keep the current admin login.
                        </span>
                      </span>
                    </label>
                    <p className="text-sm font-medium text-foreground pt-2 border-t">Keep current values for:</p>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preserveEmailTemplates}
                        onChange={(e) => setPreserveEmailTemplates(e.target.checked)}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Email Templates (don't overwrite from backup)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preserveWorkflowTemplates}
                        onChange={(e) => setPreserveWorkflowTemplates(e.target.checked)}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">Workflow Templates (don't overwrite from backup)</span>
                    </label>
                  </div>
                  <div className="p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-800 dark:text-amber-200 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Warning: This will replace all existing data with the contents of the backup file.
                    </p>
                  </div>
                </div>
                {restoreLoading && restoreProgress && (
                  <div className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground" data-testid="restore-progress-line">
                    <span className="font-medium">Progress:</span> {restoreProgress}
                  </div>
                )}
                <DialogFooter>
                  <Button variant="outline" onClick={() => setRestoreDialogOpen(false)}>Cancel</Button>
                  <Button onClick={restoreDatabase} disabled={!restoreFile || restoreLoading}>
                    {restoreLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Restore
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Point-in-Time Recovery Dialog (admin only, lazy-mounted) */}
            <PointInTimeRecoveryDialog
              open={pitrDialogOpen}
              onOpenChange={setPitrDialogOpen}
              onComplete={() => {
                // Surface in the existing restore-status panel by polling backups list.
                if (typeof loadBackups === 'function') loadBackups();
              }}
            />

            <LegacyReconcileDialog
              open={reconcileDialogOpen}
              onOpenChange={setReconcileDialogOpen}
            />

          </TabsContent>
        )}

        {/* Calendar Tab (Admin Only) */}
        {isAdmin && (
          <TabsContent value="calendar" className="mt-6 space-y-6">
            <Card className={cn(isBarbiePink && "card-brutalist")}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-primary" />
                  Google Calendar Configuration
                </CardTitle>
                <CardDescription>
                  Configure the Google Calendar to display on the Dashboard and Calendar page
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="calendar-id">Calendar ID</Label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Enter the Google Calendar ID (usually the email address associated with the calendar)
                  </p>
                  <div className="flex gap-2">
                    <Input
                      id="calendar-id"
                      placeholder="admin@gsimaging.com.au"
                      value={calendarId}
                      onChange={(e) => setCalendarId(e.target.value)}
                      className="flex-1"
                      data-testid="calendar-id-input"
                    />
                    <Button 
                      onClick={saveCalendarConfig}
                      disabled={calendarLoading}
                      data-testid="save-calendar-config-btn"
                    >
                      {calendarLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : null}
                      Save
                    </Button>
                  </div>
                </div>
                
                <div className="p-4 bg-muted/50 rounded-lg">
                  <h4 className="font-medium text-sm mb-2">Current Status</h4>
                  <div className="flex items-center gap-2">
                    {calendarId ? (
                      <>
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span className="text-sm">Calendar configured: {calendarId}</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="w-4 h-4 text-yellow-500" />
                        <span className="text-sm">No calendar configured</span>
                      </>
                    )}
                  </div>
                </div>
                
                <div className="text-xs text-muted-foreground">
                  <strong>Note:</strong> Users must have Gmail connected (in Profile tab) to view calendar events. 
                  The calendar uses the same Google OAuth credentials as Gmail integration.
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Reports Tab (Admin Only) */}
        {isAdmin && (
          <>
            <TabsContent value="reports" className="mt-6 space-y-6">
            {/* Daily Report Schedule Info */}
            <Card className={cn(isBarbiePink && "card-brutalist")}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-blue-500" />
                  Automatic Daily Reports
                </CardTitle>
                <CardDescription>
                  Daily reports are sent Mon-Fri at 7:00 PM Sydney time
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Clock className="w-4 h-4" />
                  <span>Schedule: Monday-Friday, 7:00 PM AEST/AEDT</span>
                </div>
                <div className="text-sm">
                  <strong>Report includes:</strong>
                  <ul className="list-disc list-inside ml-2 text-muted-foreground">
                    <li>Workflows created and milestones completed</li>
                    <li>Overdue and near-due milestones</li>
                    <li>Email summary</li>
                    <li>Database backup attachment</li>
                  </ul>
                </div>

                {/* Gmail diagnostic — quick self-check */}
                <div className="pt-4 border-t">
                  <Label className="text-sm font-medium">Gmail Send Diagnostic</Label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Sends a one-line test email to confirm your Gmail integration is actually delivering. Use this if Email Audit shows "sent" but recipients aren't receiving.
                  </p>
                  <div className="flex gap-2 items-center">
                    <Input
                      placeholder="Recipient (defaults to your account)"
                      value={diagRecipient}
                      onChange={(e) => setDiagRecipient(e.target.value)}
                      className="flex-1"
                      data-testid="gmail-diag-recipient"
                    />
                    <Button
                      size="sm"
                      onClick={runGmailDiagnostic}
                      disabled={diagRunning}
                      data-testid="gmail-diag-run-btn"
                    >
                      {diagRunning ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                      Run diagnostic
                    </Button>
                  </div>
                  {diagResult && (
                    <div
                      className={cn(
                        'mt-3 p-3 rounded border text-xs',
                        diagResult.test_send_status === 'sent'
                          ? 'border-green-300 bg-green-50 dark:bg-green-950/30 text-green-900 dark:text-green-200'
                          : diagResult.test_send_status === 'failed'
                            ? 'border-red-300 bg-red-50 dark:bg-red-950/30 text-red-900 dark:text-red-200'
                            : 'border-amber-300 bg-amber-50 dark:bg-amber-950/30 text-amber-900 dark:text-amber-200'
                      )}
                      data-testid="gmail-diag-result"
                    >
                      <div className="font-mono">
                        <div>has_token: {String(diagResult.has_token)}</div>
                        {diagResult.token_email && <div>token_email: {diagResult.token_email}</div>}
                        {diagResult.token_expired !== null && (
                          <div>token_expired: {String(diagResult.token_expired)}</div>
                        )}
                        <div>test_send_status: {diagResult.test_send_status}</div>
                        {diagResult.gmail_message_id && (
                          <div>gmail_message_id: {diagResult.gmail_message_id}</div>
                        )}
                        {diagResult.test_send_error && (
                          <div className="mt-1 break-words">error: {diagResult.test_send_error}</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Email Recipients Management */}
                <div className="pt-4 border-t">
                  <Label className="text-sm font-medium">Daily Report Recipients</Label>
                  <p className="text-xs text-muted-foreground mb-3">
                    These email addresses will receive the automatic daily reports
                  </p>
                  
                  <div className="flex gap-2 mb-3">
                    <Input
                      placeholder="Add email address..."
                      value={newRecipient}
                      onChange={(e) => setNewRecipient(e.target.value)}
                      className="flex-1"
                      data-testid="add-recipient-input"
                      onKeyDown={(e) => e.key === 'Enter' && addRecipient()}
                    />
                    <Button 
                      onClick={addRecipient}
                      size="sm"
                      data-testid="add-recipient-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add
                    </Button>
                  </div>
                  
                  {reportRecipients.length > 0 ? (
                    <div className="space-y-2">
                      {reportRecipients.map((email) => (
                        <div key={email} className="flex items-center justify-between p-2 bg-muted rounded-lg">
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm">{email}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => sendReportToRecipient(email)}
                              disabled={sendingToRecipient === email || !gmailStatus?.connected}
                              className="h-7 px-2"
                              data-testid={`send-to-${email.replace(/[@.]/g, '-')}`}
                            >
                              {sendingToRecipient === email ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <Send className="w-3 h-3" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeRecipient(email)}
                              className="text-destructive hover:text-destructive h-7 px-2"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No recipients configured. Default admin email will be used.
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Send Report Now */}
            <Card className={cn(isBarbiePink && "card-brutalist")}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Send className="w-5 h-5 text-green-500" />
                  Send Report On-Demand
                </CardTitle>
                <CardDescription>
                  Send an instant report to any email address
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter email address"
                    value={reportEmail}
                    onChange={(e) => setReportEmail(e.target.value)}
                    className="flex-1"
                    data-testid="report-email-input"
                  />
                  <Button 
                    onClick={sendReportNow} 
                    disabled={reportLoading || !gmailStatus?.connected}
                    data-testid="send-report-btn"
                  >
                    {reportLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Send Report
                  </Button>
                </div>
                {!gmailStatus?.connected && (
                  <p className="text-sm text-amber-600 flex items-center gap-1 mt-2">
                    <AlertTriangle className="w-4 h-4" />
                    Connect Gmail in the Gmail tab to send reports
                  </p>
                )}
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={previewReport}
                    disabled={reportLoading}
                    data-testid="preview-report-btn"
                  >
                    Preview Report
                  </Button>
                </div>
                {reportPreview && (
                  <div className="mt-4">
                    <Label>Report Preview</Label>
                    <pre className="mt-2 p-4 bg-muted rounded-lg text-xs whitespace-pre-wrap max-h-80 overflow-auto">
                      {reportPreview}
                    </pre>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="ai-provider" className="mt-6 space-y-6">
            <AIProviderSettings isBarbiePink={isBarbiePink} />
          </TabsContent>

          <TabsContent value="error-logs" className="mt-6 space-y-6">
            <ErrorLogsPanel />
          </TabsContent>

          <TabsContent value="saved-search-audit" className="mt-6 space-y-6">
            <SavedSearchAuditPanel />
          </TabsContent>

          <TabsContent value="feature-flags" className="mt-6 space-y-6">
            <FeatureFlagsPanel isBarbiePink={isBarbiePink} />
          </TabsContent>
          </>
        )}
      </Tabs>

      {/* Delete Backup Confirmation */}
      <AlertDialog open={!!backupToDelete} onOpenChange={() => setBackupToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Backup?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete backup file: <strong>{backupToDelete}</strong>?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteBackup} className="bg-destructive hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* XLS Import Dialog - Multi-step with Field Mapping */}
      <Dialog open={xlsImportDialogOpen} onOpenChange={(open) => { if (!open) cancelXlsImport(); else setXlsImportDialogOpen(true); }}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              Import Organizations from Excel
            </DialogTitle>
            <DialogDescription>
              {smartImportStep === 1 && "Step 1: Upload your Excel file"}
              {smartImportStep === 2 && "Step 2: Select the sheet containing organization data"}
              {smartImportStep === 3 && "Step 3: Map Excel columns to database fields"}
              {smartImportStep === 4 && "Import Complete!"}
            </DialogDescription>
          </DialogHeader>
          
          {/* Step indicators */}
          <div className="flex items-center justify-center gap-2 py-2">
            {[1, 2, 3, 4].map((step) => (
              <div key={step} className="flex items-center">
                <div className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium",
                  smartImportStep >= step ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                )}>
                  {step === 4 && smartImportStep === 4 ? <Check className="w-4 h-4" /> : step}
                </div>
                {step < 4 && <div className={cn("w-8 h-0.5", smartImportStep > step ? "bg-primary" : "bg-muted")} />}
              </div>
            ))}
          </div>
          
          {/* Step 1: Upload */}
          {smartImportStep === 1 && (
            <div className="space-y-4">
              <div className="border-2 border-dashed rounded-lg p-8 text-center">
                <Upload className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-sm text-muted-foreground mb-4">
                  Upload an Excel file (.xlsx) containing your organization data
                </p>
                <input type="file" accept=".xlsx,.xls" onChange={handleXlsUpload} className="hidden" id="xls-upload" />
                <label htmlFor="xls-upload">
                  <Button asChild disabled={xlsUploadLoading}>
                    <span>
                      {xlsUploadLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                      Select File
                    </span>
                  </Button>
                </label>
              </div>
            </div>
          )}
          
          {/* Step 2: Select Sheet */}
          {smartImportStep === 2 && xlsPreviewData && (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div className="flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5 text-green-600" />
                  <span className="font-medium">{xlsPreviewData.original_filename}</span>
                </div>
                <Button variant="ghost" size="sm" onClick={() => { setSmartImportStep(1); setXlsPreviewData(null); }}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="space-y-2">
                <label className="text-sm font-medium">Select a sheet to import:</label>
                <div className="grid gap-2">
                  {Object.entries(xlsPreviewData.sheets).map(([sheetName, sheetData]) => (
                    <div 
                      key={sheetName} 
                      className={cn(
                        "p-4 border rounded-lg cursor-pointer hover:border-primary transition-colors",
                        selectedImportSheet === sheetName && "border-primary bg-primary/5"
                      )}
                      onClick={() => setSelectedImportSheet(sheetName)}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium">{sheetName}</h4>
                          <p className="text-sm text-muted-foreground">{sheetData.row_count} rows, {sheetData.columns.length} columns</p>
                        </div>
                        {selectedImportSheet === sheetName && <Check className="w-5 h-5 text-primary" />}
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        Columns: {sheetData.columns.slice(0, 5).join(', ')}{sheetData.columns.length > 5 ? '...' : ''}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          {/* Step 3: Field Mapping */}
          {smartImportStep === 3 && xlsPreviewData && selectedImportSheet && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">Map Excel Columns to Database Fields</h4>
                <Button variant="outline" size="sm" onClick={autoMapFields}>
                  <Wand2 className="w-4 h-4 mr-2" />
                  Auto-Map
                </Button>
              </div>
              
              {dbFields.length === 0 ? (
                <div className="text-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">Loading database fields...</p>
                </div>
              ) : (
                <>
                  <div className="border rounded-lg divide-y max-h-60 overflow-y-auto">
                    {(xlsPreviewData.sheets[selectedImportSheet]?.columns || []).map((xlsCol) => (
                      <div key={xlsCol} className="flex items-center gap-4 p-3">
                        <div className="flex-1">
                          <span className="text-sm font-medium">{xlsCol}</span>
                          {xlsPreviewData.sheets[selectedImportSheet]?.preview_rows?.[0] && (
                            <p className="text-xs text-muted-foreground truncate">
                              e.g., {String(xlsPreviewData.sheets[selectedImportSheet].preview_rows[0][xlsCol] ?? 'N/A').substring(0, 30)}
                            </p>
                          )}
                        </div>
                        <ArrowRight className="w-4 h-4 text-muted-foreground" />
                        <div className="w-48">
                          <Select 
                            value={fieldMappings[xlsCol] || ''} 
                            onValueChange={(value) => setFieldMappings(prev => ({...prev, [xlsCol]: value}))}
                          >
                            <SelectTrigger className="text-sm">
                              <SelectValue placeholder="Skip this column" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="">Skip this column</SelectItem>
                              {dbFields.map(field => (
                                <SelectItem key={field.field} value={field.field}>
                                  {field.label} {field.required && '*'}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <Separator />
                  
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium">Match Organizations By:</label>
                      <Select value={matchField} onValueChange={setMatchField}>
                        <SelectTrigger className="w-64 mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="org_name">Organization Name (recommended)</SelectItem>
                          <SelectItem value="contact_email">Contact Email</SelectItem>
                          <SelectItem value="org_id">Organization ID</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground mt-1">
                        Existing organizations will be matched using this field
                      </p>
                    </div>
                    
                    <div className="flex gap-6">
                      <label className="flex items-center gap-2">
                        <input type="checkbox" checked={updateExisting} onChange={(e) => setUpdateExisting(e.target.checked)} />
                        <span className="text-sm">Update existing organizations</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input type="checkbox" checked={createNew} onChange={(e) => setCreateNew(e.target.checked)} />
                        <span className="text-sm">Create new organizations if not found</span>
                      </label>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
          
          {/* Step 4: Results */}
          {smartImportStep === 4 && importResults && (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-4">
                <div className="p-4 bg-blue-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-blue-600">{importResults.matched}</p>
                  <p className="text-sm text-muted-foreground">Matched</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-600">{importResults.updated}</p>
                  <p className="text-sm text-muted-foreground">Updated</p>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-purple-600">{importResults.created}</p>
                  <p className="text-sm text-muted-foreground">Created</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-gray-600">{importResults.skipped}</p>
                  <p className="text-sm text-muted-foreground">Skipped</p>
                </div>
              </div>
              
              {importResults.errors?.length > 0 && (
                <div className="p-3 bg-destructive/10 rounded-lg">
                  <p className="text-sm font-medium text-destructive mb-2">Errors:</p>
                  <ul className="text-xs text-destructive space-y-1">
                    {importResults.errors.slice(0, 5).map((err, i) => (
                      <li key={i}>Row {err.row}: {err.error}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {importResults.details?.length > 0 && (
                <div className="border rounded-lg max-h-40 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-muted">
                      <tr>
                        <th className="px-2 py-1 text-left">Row</th>
                        <th className="px-2 py-1 text-left">Organization</th>
                        <th className="px-2 py-1 text-left">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importResults.details.slice(0, 50).map((detail, i) => (
                        <tr key={i} className="border-t">
                          <td className="px-2 py-1">{detail.row}</td>
                          <td className="px-2 py-1">{detail.org_name || 'N/A'}</td>
                          <td className="px-2 py-1">
                            <Badge variant={detail.status === 'updated' ? 'default' : detail.status === 'created' ? 'secondary' : 'outline'}>
                              {detail.status}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            {smartImportStep > 1 && smartImportStep < 4 && (
              <Button variant="outline" onClick={() => setSmartImportStep(prev => prev - 1)}>
                Back
              </Button>
            )}
            <Button variant="outline" onClick={cancelXlsImport}>
              {smartImportStep === 4 ? 'Close' : 'Cancel'}
            </Button>
            {smartImportStep === 2 && (
              <Button onClick={() => { setSmartImportStep(3); autoMapFields(); }} disabled={!selectedImportSheet}>
                Next: Map Fields
              </Button>
            )}
            {smartImportStep === 3 && (
              <Button onClick={executeSmartImport} disabled={xlsImportLoading || Object.keys(fieldMappings).filter(k => fieldMappings[k]).length === 0}>
                {xlsImportLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                Import Data
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
